/**
 * Materialize a manually-drawn cabinet run into cabinet / filler /
 * corner / appliance / countertop objects + NKBA warnings. Pure. See
 * claude-docs/2026-06-13-manual-cabinet-runs-design.md §3–§5 and the
 * override model in claude-docs/2026-06-19-cabinet-run-override-model.md.
 *
 * Per segment: corner reservations trim the fillable span; appliances
 * partition it into sub-spans; each sub-span is packed as a sequence —
 * fixed-width bays placed (and slid to stay flush), auto bays filling the
 * gaps, and per-bay appearance applied. Fixed widths are locked but NOT
 * positions, so bays slide instead of overlapping.
 */

const { planSubSpanSequence, strandedFillerIndices } = require('./spanPacking');
const {
  isCorner,
  isObliqueCorner,
  obliqueStop,
  cornerLegs,
  buildObliqueKite,
  segmentFillBounds,
  buildCornerUnits,
  buildCornerUppers,
  buildCornerCounterItems,
  buildSingleSidedCornerUnit,
  CORNER_CABINET_FOOTPRINT,
} = require('./corners');
const { seamCornerSlabs } = require('./cornerSlabSeams');
const {
  isOutsideBend,
  planOutsideBend,
  buildOutsideWedgeBody,
} = require('./outsideBend');
const {
  profileOf,
  uppersEnabled,
  runBodyHeight,
  counterTopOffsetFor,
  VANITY_PROFILE_ID,
} = require('./profiles');
const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { baysForRun, keepOutBays, KEEP_OUT_BAY_KINDS } = require('./bays');
const { resolveRunBayFootprints, scannedColumnFor } = require('./bayFootprint');
const {
  planApplianceEndPanels,
  APPLIANCE_END_PANEL_WIDTH,
  APPLIANCE_END_PANEL_MIN_WIDTH,
  NEEDS_END_PANEL,
} = require('../kitchen-assembly/applianceEndPanel');
const { buildBlankPanel } = require('../kitchen-assembly/buildBlankPanel');
const { legStopClearingBody } = require('../kitchen-assembly/cornerDetection');
const {
  familyFillOpts,
  cornerBlindLength,
  cornerBlindSet,
  cornerLSet,
  familyOf,
} = require('./families');
const { familySizes, drawerStackFor } = require('./vendorSizes');
const { kindFitWidthIn } = require('./bayWidths');
const {
  fridgeColumnPanels,
  fridgeColumnDepth,
  fridgeColumnLineY,
  fridgeIsBuiltIn,
} = require('./fridgeColumn');
const {
  blindSizeChoices,
  fitsBlindSize,
} = require('../kitchen-assembly/cornerBlind');
const { MIN_FILLER_IN } = require('./moduleSolver');
const { scribeCounterItems } = require('./scribeCounter');
const {
  absorbLeftoverIntoReveals,
  widenSinkBasesIntoReveals,
} = require('./cornerReveal');
const { nextSinkBaseWidthIn } = require('./sinkBaseWidths');
const { applyLinkAnchors } = require('./linkAnchors');
const {
  reservationsForSegment,
  blockingOpeningRangesForAppliance,
  buildApplianceObjects,
  computeWarnings,
  footprintForAppliance,
} = require('./appliances');
const {
  openingReservationsForSegment,
  applianceOpeningOverlaps,
  allOpeningReservationsForSegment,
  adjacentOpeningReservationsForSegment,
  tallItemOpeningConflicts,
  cornerPlan,
  deadCornerLeg,
  measureCorner,
  lFits,
} = require('./openings');
const {
  userLowerTreatment,
  gradeLowerCorner,
} = require('./lowerCornerTreatment');
const { optionIdOf, blindSizeReport } = require('./cornerChoice');
const {
  overridesForZone,
  governingOverride,
  isTallOverride,
  cornerOverride,
  applyBayType,
  effectiveAppearance,
  applyAppearanceBundle,
} = require('./bayOverrides');
const {
  buildLowerCabinetForSegment,
} = require('../kitchen-assembly/buildLowerCabinet');
const {
  BASE_HEIGHT,
  BASE_HEIGHT_TOLERANCE,
  cornerBoxFor,
  segmentCabinetDepth,
  segmentCornerDepth,
} = require('./runDepth');
const {
  cornerTreatmentOverride,
  planFromTreatment,
} = require('./cornerTreatment');
const { fillUppers, dropLoneFillerUppers } = require('./upperTier');
const { upperBandFor, columnTopY, hoodChimneyTopY } = require('./upperBand');
const { homedRun, previousSpansOf } = require('./overrideHomes');
const { drawGlassDoors } = require('./glassUppers');
const { stampRollOuts } = require('./rollOuts');
const { vendorSinkFronts } = require('./sinkUnits');
const { fillStackRow } = require('./upperStack');
const {
  buildCountertopRuns,
} = require('../kitchen-assembly/buildCountertopRuns');
const { seatRunArms } = require('../kitchen-assembly/seatedPeninsulaTop');
const {
  buildKitchenSinkBasin,
  buildKitchenFaucet,
} = require('../kitchen-assembly/buildKitchenSink');
const {
  buildVanitySinkBasin,
  buildVanityFaucet,
} = require('../vanity-assembly/buildVanitySink');
const {
  vanityCounterFrontOverhang,
} = require('../vanity-assembly/counterOverhang');
const { standsAtMeasuredDepth } = require('./sizing');

const { SPAN_EPS } = require('./spanPacking');
const { stampEndShelfSides } = require('./endShelves');
const { stampGlassHolders } = require('./glassHolders');
const {
  stampAngledEndSides,
  isAngledEnd,
  cutCounterAtAngledEnd,
} = require('./angledEnds');
const { microwaveFor } = require('./microwaveCabinets');
const { footprintFor } = require('../geometry/objectFootprint');
const { isPointInsidePolygon } = require('../geometry/pointInPolygon');

// Floor-standing bodies whose side closes a dishwasher's beside them.
const CLOSES_A_SIDE =
  /cabinet|blank-panel|filler|range|oven|refrigerator|fridge|dishwasher|storage|cooktop|washer|dryer/;

const IN = 0.0254;

const isSinkReservation = (r) => /sink/.test(r?.type || r?.bay?.kind || '');

/** True when a segment's `end` ('start' | 'end') sits at its HIGH param. */
function vertexOnHighSide(seg, end) {
  const dir = Math.sign(seg.endParam - seg.startParam) || 1;
  return (end === 'end') === dir > 0;
}

/**
 * The hard piece (appliance bay) nearest a segment's end (its corner vertex),
 * as `{ d, piece }`: `d` is measured INTO the segment, and is Infinity with a
 * null `piece` when there is none. Feeds `cornerPlan` beside the opening
 * distance.
 */
function nearestHardPieceToEnd(run, segIndex, end, anchored = false) {
  const seg = run.segments[segIndex];
  const vertex = end === 'end' ? seg.endParam : seg.startParam;
  const highSide = vertexOnHighSide(seg, end);
  let best = { d: Infinity, piece: null };
  for (const r of reservationsForSegment(run, segIndex)) {
    // A sink is not a blocker: the corner unit wins and the sink base slides
    // clear of it (the rule below in this file). Ranges, fridges and
    // dishwashers stay where they are, so the corner yields to them.
    //
    // …unless the appliances are ANCHORED. On the scan path the sink is a
    // MEASURED object, so "the base slides clear" holds only as far as
    // `materializeScanRuns` will move the basin with it — see
    // `sinkSlackToEnd`, which is the whole of that allowance. Beyond it the
    // corner claims its full 36" leg and the sink base, built across the bay
    // where the sink actually stands, lands inside it. E5DED26F: a 94" wall
    // with a 36" corner unit at each end leaves 22", and a 36" sink base was
    // emitted at [36, 72] — 14" through the second corner unit, with the sink
    // itself 2.1" into the first. Same reasoning that gave the other
    // appliances `anchoredAppliances`; the sink was excluded before that
    // existed and the exclusion was never revisited.
    if (!anchored && isSinkReservation(r)) continue;
    // the edge of the piece nearest the vertex, along the leg
    const d = highSide ? vertex - r.hi : r.lo - vertex;
    if (d < best.d) best = { d: Math.max(0, d), piece: r };
  }
  return best;
}

/**
 * How far an ANCHORED sink base may slide AWAY from a corner to let the
 * corner in: the room beside it, capped at MAX_SCAN_APPLIANCE_SLIDE.
 *
 * Every other appliance on the scan path is a measured object the run never
 * moves, and the corner yields to it (`cornerPlan`). A sink is the one piece
 * the engine's own rule already lets a corner displace ("the corner unit wins
 * and the sink base slides clear"), and a scanned basin's position is soft:
 * RoomPlan localises a small fixture loosely, and in the built kitchen the
 * base under it lands where the cabinet grid puts it. So a scanned sink gives
 * a corner up to the same 6" post-processing already treats as the boundary
 * between scan noise and a real discrepancy, and `materializeScanRuns` moves
 * the basin, faucet and host with the bay. Anchorage 2 / Scan 1: a sink base
 * 23.1" from the vertex — 0.9" under the 24" blind-corner gate — left a bare
 * 24" × 24" corner with no cabinet and no countertop, and the base shoved 0.9"
 * off its own basin.
 *
 * The slack is real room, not a promise: it stops at the next hard piece or
 * opening keep-out beyond the sink on the same leg, and at the leg's far fill
 * bound — otherwise the packer clamps the base into a dishwasher (or the
 * other corner) and the basin cannot follow. `farBound` is that far fill
 * bound (`farFillBound`).
 */
function sinkSlackToEnd(run, segIndex, end, ctx, piece, farBound) {
  if (!piece || !isSinkReservation(piece)) return 0;
  const room = sinkSlideRoom(run, segIndex, end, ctx, piece, farBound);
  return Math.max(0, Math.min(MAX_SCAN_APPLIANCE_SLIDE, room));
}

/**
 * The real room a sink base has to slide AWAY from a corner vertex: to the
 * next hard piece or opening keep-out beyond it on the same leg, or to the
 * leg's far fill bound (`farFillBound`), uncapped.
 */
function sinkSlideRoom(run, segIndex, end, ctx, piece, farBound) {
  const seg = run.segments[segIndex];
  const highSide = vertexOnHighSide(seg, end);
  const mid = (piece.lo + piece.hi) / 2;
  // the base's edge AWAY from the vertex, and how far it may go
  const farEdge = highSide ? piece.lo : piece.hi;
  let room = highSide ? farEdge - farBound : farBound - farEdge;
  const beyond = [
    ...reservationsForSegment(run, segIndex).filter(
      (r) => r.bayId !== piece.bayId
    ),
    ...openingReservationsForSegment(run, segIndex, ctx),
    ...adjacentOpeningReservationsForSegment(run, segIndex, ctx),
  ];
  for (const r of beyond) {
    const rMid = (r.lo + r.hi) / 2;
    // only what stands on the far side of the base bounds its slide
    if (highSide ? rMid >= mid : rMid <= mid) continue;
    room = Math.min(room, highSide ? farEdge - r.hi : r.lo - farEdge);
  }
  return Math.max(0, room);
}

/**
 * On the GENERATED path a sink base is not a corner blocker: the corner unit
 * wins and the packer slides the base clear of the 36" footprint. That holds
 * only while there is room on the sink's far side to slide into. When
 * another hard piece (a dishwasher tiled edge to edge with it) or the leg's
 * far bound leaves less room than the slide needs, the clamp drives the base
 * INTO that neighbour — `planSubSpanSequence` reports overlapping hard
 * pieces, it never resolves them — and the mover then carries the basin
 * along. Golden West / Scan 1: a 36" sink base ending 27" from the vertex,
 * dishwasher hard against it, re-solved 9" over the dishwasher on its first
 * appearance edit. Such a sink is PINNED: it counts at its own distance and
 * `cornerPlan` gates that leg at the full footprint, so the corner grades
 * blind (24") or empty instead of forcing the unit and the slide.
 *
 * Returns the nearest pinned sink as `{ d, piece }`, or null.
 */
function pinnedSinkToEnd(run, segIndex, end, ctx, farBound) {
  const seg = run.segments[segIndex];
  const vertex = end === 'end' ? seg.endParam : seg.startParam;
  const highSide = vertexOnHighSide(seg, end);
  let best = null;
  for (const r of reservationsForSegment(run, segIndex)) {
    if (!isSinkReservation(r)) continue;
    const d = Math.max(0, highSide ? vertex - r.hi : r.lo - vertex);
    if (d >= CORNER_CABINET_FOOTPRINT - SPAN_EPS) continue;
    const room = sinkSlideRoom(run, segIndex, end, ctx, r, farBound);
    if (room >= CORNER_CABINET_FOOTPRINT - d - SPAN_EPS) continue; // slides clear
    if (!best || d < best.d) best = { d, piece: r };
  }
  return best;
}

/**
 * The fill bound of segment `segIndex` at the end AWAY from `end`. Corners
 * are planned in order, so a leg's start-side trim is already known when its
 * end is being planned; the other way round the far corner is still to come
 * and its full footprint is the conservative reservation.
 */
function farFillBound(segments, segIndex, end, cornerTrims) {
  const seg = segments[segIndex];
  const dir = Math.sign(seg.endParam - seg.startParam) || 1;
  if (end === 'end') return seg.startParam + dir * cornerTrims[segIndex].start;
  const reserve =
    segIndex < segments.length - 1 && isCorner(seg, segments[segIndex + 1])
      ? CORNER_CABINET_FOOTPRINT
      : 0;
  return seg.endParam - dir * reserve;
}

/**
 * How far the EXTENDING leg of a blind corner runs clear from its vertex: to
 * the first appliance bay or opening keep-out on that leg, or to the leg's
 * far fill bound (`farFillBound`). What a single-sided corner unit may take.
 */
function freeRunFromVertex(run, segIndex, end, ctx, cornerTrims) {
  const seg = run.segments[segIndex];
  const vertex = end === 'end' ? seg.endParam : seg.startParam;
  const highSide = vertexOnHighSide(seg, end);
  let free = Math.abs(
    vertex - farFillBound(run.segments, segIndex, end, cornerTrims)
  );
  const pieces = [
    ...reservationsForSegment(run, segIndex),
    ...openingReservationsForSegment(run, segIndex, ctx),
    ...adjacentOpeningReservationsForSegment(run, segIndex, ctx),
  ];
  for (const r of pieces) {
    const d = highSide ? vertex - r.hi : r.lo - vertex;
    free = Math.min(free, Math.max(0, d));
  }
  return free;
}

/**
 * The length of the single-sided corner unit a blind corner's extending leg
 * gets, or 0 when the leg cannot host one (under the standard 36"). Metres in,
 * metres out — a thin unit conversion over `cornerBlindLength`, which owns the
 * rule and keys it on the run's FAMILY.
 *
 * This used to be one continuous stretch from 36" to 48" for every run. That
 * is still exactly what a Custom family does; a Semi-Custom one lands on the
 * 3" grid and a Stock one on {36, 48}, each letting its own filler cover the
 * rest, because "which lengths exist" is the axis families are FOR.
 */
function singleSidedLength(free, run) {
  return cornerBlindLength(run, free / INCHES_TO_METERS) * INCHES_TO_METERS;
}
const {
  MIN_CABINET_WIDTH,
  MIN_STANDALONE_CABINET_WIDTH,
  MIN_FILL_CABINET_WIDTH,
  MAX_SCAN_APPLIANCE_SLIDE,
  KITCHEN_CABINET_DIMENSIONS,
  INCHES_TO_METERS,
  FRIDGE_WALL_FILLER_MIN,
  INSIDE_CORNER_REVEAL,
} = require('../kitchen-assembly/constants');

function tagBay(obj, runId, bayIndex) {
  obj.addedBy = 'manual-cabinet-run';
  obj.runId = runId;
  obj.bayIndex = bayIndex;
}

/**
 * A tall span's box: floor to the upper-cabinet line, at the run's depth.
 * The height is the line the fill would hang uppers to — not the uppers it
 * emitted, which do not exist until the lower pass is done and are absent
 * under a keep policy — so a span and the uppers beside it always meet. With
 * a stacked row it is the STACK's top (`columnTopY`, plan C19).
 */
function tallSpanSpec(floorY, ctx) {
  const band = columnTopY(ctx.upperBand) - floorY;
  const heights = ctx.tallHeightsIn;
  if (!heights || !heights.length)
    return { height: band, depth: ctx.cabinetDepth };
  // A vendor family stands a tall at a height it makes: the tallest that fits
  // under the line, else its shortest (a pantry is never cut down).
  const fits = heights.filter((h) => h * INCHES_TO_METERS <= band + 1e-6);
  const inches = fits.length ? fits[fits.length - 1] : heights[0];
  return { height: inches * INCHES_TO_METERS, depth: ctx.cabinetDepth };
}

/**
 * A FRIDGE bay's column: the surround `buildFridgeInstall` wraps the appliance
 * in, so a scribe beside it dies into the same top and the same face.
 *
 * The fridge is not a `tallUnit` bay, but the run builds it one — a full-height
 * surround plus an over-fridge cabinet — and every other pass already reads it
 * that way (`materializeRun`'s over-fridge sky, `applianceEndPanel`'s "a fridge
 * gets a surround"). Only the filler beside it did not, so a 7" reveal between a
 * fridge and the run's end came out as a 34.5" false panel under a stub of
 * countertop, with five feet of bare wall over it and the fridge column full
 * height alongside.
 *
 * **Only where the run actually builds one.** A SCAN run's install is MEASURED
 * and kept (`policy.rebuildInstall` / `emitAppliances` are both false there, so
 * `materializeRun` drops the surround this fill emits), and the scan does not
 * always measure one: 08E54F9E has a bare 72" refrigerator with a 6.3" reveal
 * beside it and no surround at all. Matching a column the room will not contain
 * would stand a 103.7" strip next to a 6' box — worse than the base filler it
 * replaced. So `ctx.buildsFridgeColumn === false` measures what is actually
 * there — `scannedColumnFor`: the surround where one wraps the fridge, the
 * fridge otherwise — the same "match what you die into" the `tallUnit` branch
 * above applies to a measured pantry. Absent (the engine called directly) means
 * the run emits its own install, which `fillRun` always does.
 *
 * Where the run DOES build it, the rule is the install's: the run's upper line,
 * never below the fridge itself, capped at the ceiling; uppers OFF means the
 * column runs to the ceiling. The line is read from the run's `upperBand` rather than
 * the uppers the fill emits, exactly as `tallSpanSpec` does and for the same
 * reason — the lower pass runs first, so the emitted uppers do not exist yet.
 * The install measures them instead; they are the same line, and the one case
 * where they could disagree (uppers on, none emitted) is resolved at that call
 * site, not papered over here.
 *
 * Null without a ceiling — `buildFridgeInstall` builds no surround at all then,
 * so a run that would otherwise build one has no column either.
 */
function fridgeColumnSpec(run, floorY, bay, ctx) {
  // A free-standing fridge has no column either: what stands there is the
  // fridge, so the filler beside it matches the fridge, as on a scan run.
  if ((ctx && ctx.buildsFridgeColumn === false) || !fridgeIsBuiltIn(bay)) {
    // Measured, so measure it: `scannedColumnFor` reads the surround wrapping
    // the fridge, else the fridge. The BAY cannot answer here — the scan policy
    // skips the footprint pass and the deriver stores no height at all.
    const col = scannedColumnFor(bay, ctx.existingObjects);
    // Nothing measured, nothing to match. The stored bay is NOT the fallback:
    // `scanApplianceAnchors` records no height, so what it carries is the
    // generic tier default (70") — a number, not a finding.
    if (!col) return null;
    return {
      height: col.center.y + col.dimensions.y / 2 - floorY,
      depth: col.dimensions.z || bay.depth,
    };
  }
  const ceilingY = ctx && ctx.ceilingY;
  if (typeof ceilingY !== 'number') return null;
  const bayTop = floorY + (bay.height || 0);
  const topY = !uppersEnabled(run)
    ? ceilingY
    : Math.min(
        ceilingY,
        Math.max(fridgeColumnLineY(run, ctx.upperBand, floorY), bayTop)
      );
  // the column's face is its panels' — as deep as the fridge's type asks, or
  // a vendor's panel depth (`fridgeColumnDepth`) — and a deeper fridge
  // stands proud of it
  return {
    height: topY - floorY,
    depth: fridgeColumnDepth(familyOf(run), bay.depth),
  };
}

/**
 * The override that governs a packed piece: a fixed piece's own, the
 * type-only override anchored inside an auto bay's range, none for a filler.
 * The ONE derivation `emitBay` (front + height) and `tallNeighbourOf` (is my
 * neighbour tall?) share — RP27: an auto bay made tall by a width-less
 * override is a tall neighbour exactly like a fixed module or a scanned
 * pantry, so the scribe beside it rises with it.
 */
function governingOverrideForPiece(run, segIndex, piece) {
  if (!piece || piece.kind === 'filler' || piece.kind === 'hard') return null;
  return governingOverride(
    run,
    segIndex,
    { start: piece.start, end: piece.end },
    piece.override || null,
    'lower',
    piece.claimed || null
  );
}

/**
 * Build + record one bay. `kind` ∈ 'fixed' | 'auto' | 'filler'. `override` is
 * the bay's own width override (for 'fixed') so its appearance wins; auto bays
 * inherit appearance from any override anchored within their span.
 * `tallNeighbour` is the tall unit a FILLER dies into (`tallFillerFor`); a bay
 * governed by a tall override is tall on its own.
 */
function emitBay(
  wall,
  piece,
  floorY,
  run,
  segIndex,
  state,
  kind,
  override,
  tallNeighbour
) {
  // `claimed`: the fronts this zone already built a box for (`fitBaysToLine`)
  // — read for the governing override only, never carried onto the range the
  // countertop and the uppers keep.
  const range = { start: piece.start, end: piece.end };
  // The override governing this bay is resolved FIRST: it decides the front,
  // and for a tall override the height too, so the box is built once from one
  // spec. A filler is governed by nothing — but one INSIDE a tall span (the
  // span's remainder) still answers to the span's override, below.
  const gov = governingOverrideForPiece(run, segIndex, {
    kind,
    start: range.start,
    end: range.end,
    override,
    claimed: piece.claimed,
  });
  const tall =
    tallNeighbour ||
    (isTallOverride(gov) ? tallSpanSpec(floorY, state.ctx) : null);
  // The run's dimension PROFILE supplies body depth/height and the label; a
  // measured depth (tall span / island / scan) still wins, and a tall box's
  // height is applied below, after the build. For the kitchen profile every
  // value equals the builder's own default, so output is unchanged. A drawn
  // island leg filled with the run it turns off carries its own row depth
  // (`segmentCabinetDepth`).
  const profile = state.profile;
  const cabinetDepth =
    (tall && tall.depth) ||
    segmentCabinetDepth(run.segments[segIndex], state.ctx, profile);
  const { cabinet } = buildLowerCabinetForSegment(wall, range, floorY, {
    depth: cabinetDepth,
    height: runBodyHeight(run),
    label: profile.lowerLabel,
  });
  if (!cabinet) return;

  // A tall box — a span's module, or a filler closing a gap beside a tall
  // unit — rises to the upper line. A tall filler is a tall scribe strip, not
  // a 6"-wide base cabinet with a stub of countertop on it and five feet of
  // bare wall above; the run's countertop and its uppers both skip it below.
  if (tall) {
    cabinet.dimensions = { ...cabinet.dimensions, y: tall.height };
    cabinet.center = { ...cabinet.center, y: floorY + tall.height / 2 };
  }

  if (kind === 'filler') {
    cabinet.label = tall ? 'Tall Filler' : 'Cabinet Filler';
    cabinet.category = { 'lower-cabinet': true, filler: true };
    // Render as a flat false-panel (no door / handle), not a mini door-cabinet.
    cabinet.columns = [{ type: 'false-panel', width: cabinet.dimensions.x }];
    // The remainder of a tall span answers to the span, so selecting it edits
    // the span (spanPacking echoes the override on that one filler piece).
    if (override) cabinet.overrideId = override.id;
  } else {
    applyBayType(cabinet, (gov && gov.type) || 'standard');
    if (kind === 'fixed') {
      cabinet.category = {
        ...(cabinet.category || { 'lower-cabinet': true }),
        fixedWidth: true,
      };
    }
    // Tag the bay with the override that governs it so the editor can update
    // the same override on a later edit (a slid bay's centre ≠ its anchor).
    if (gov) cabinet.overrideId = gov.id;
  }

  // Effective appearance bundle: unit override ?? run ?? room.
  applyAppearanceBundle(
    cabinet,
    effectiveAppearance({ override: gov, run, ctx: state.ctx })
  );
  // A filler is a blank slab — a framed door style (shaker/raised/glass) can't
  // render in a <3" strip, so force a flat front (the paint colour is kept).
  if (kind === 'filler') cabinet.doorStyle = 'flat';

  tagBay(cabinet, run.id, state.bayIndex);
  // Roll-out trays ride the governing bay record, so a re-pack or a wall
  // drag keeps them; stamped after `tagBay` because a scanned run's box
  // (named for its run) takes none.
  stampRollOuts(cabinet, gov, familyOf(run));
  state.bayIndex += 1;
  if (kind === 'filler') state.fillers.push(cabinet);
  else state.cabinets.push(cabinet);
  // Record the bay's param footprint so the upper pass can hang an upper at the
  // exact same position (uppers mirror the lower CABINET bays — fillers are
  // recorded but skipped by the upper pass).
  state.lowerBays.push({
    segIndex,
    start: range.start,
    end: range.end,
    kind,
    // The upper pass mirrors every lower bay, but a tall filler already
    // reaches the upper band — hanging one over it would intersect it.
    ...(tall ? { tall: true } : {}),
  });
  // A tall bay carries no countertop — and it must END the slab either side
  // of it: `groupIntoRuns` bridges any hole up to 9" (a hole between two
  // cabinets, bridged on purpose), and a 9" tall bay is not a hole.
  if (tall) {
    state.counterHeightItems.push({ wallId: wall.id, range, type: 'break' });
    return;
  }
  const counterItem = {
    wallId: wall.id,
    range,
    type: 'lower-cabinet',
    id: cabinet.id,
  };
  if (cabinet.dimensions.z != null) counterItem.depth = cabinet.dimensions.z;
  state.counterHeightItems.push(counterItem);
}

// A neighbour only counts as TALL if it rises past a base cabinet — a scanned
// box SHORTER than base height also fails `isBaseHeight`, and matching a 20"
// one would make the filler a stump.
const TALL_NEIGHBOUR_MIN_HEIGHT = BASE_HEIGHT + BASE_HEIGHT_TOLERANCE;

// Headroom test tolerance: cabinetry abuts, and a neighbour flush against the
// filler must not read as standing over it.
const TALL_FILLER_INSET = 0.5 * IN;

/**
 * Is the band above a would-be tall filler actually free?
 *
 * A scanned kitchen carries uppers the run does not own and never rebuilds, and
 * one of them routinely starts flush with the tall unit's face — an over-fridge
 * cabinet does exactly that. Growing the filler into it puts an upper through a
 * full-height cabinet, which `kitchen-rule-liveness` rightly forbids (1B988FBD:
 * a 0.5" scribe between an 89.9" pantry and the fridge surround, clipped 0.45"
 * by the 18" cabinet over the fridge).
 *
 * `ctx.tallFillerBlockers` is supplied by the SCAN materializer, which is the
 * only path with uppers the engine did not emit; on every other path the run
 * builds its own uppers and `fillUppers` already skips a tall filler, so an
 * absent list means "nothing to clear".
 */
function tallFillerClears(wall, range, tallHeight, floorY, ctx) {
  const blockers = ctx && ctx.tallFillerBlockers;
  if (!blockers || !blockers.length) return true;
  const axes = getWallAxes(wall);
  if (!axes) return true;
  const a = Math.min(range.start, range.end);
  const b = Math.max(range.start, range.end);
  // Shrink the inset on a sliver rather than skipping the test: a 0.5" scribe
  // is exactly where this matters, and insetting it by 0.5" a side left it
  // untestable and silently cleared (1B988FBD).
  const inset = Math.min(TALL_FILLER_INSET, (b - a) / 4);
  const lo = a + inset;
  const hi = b - inset;
  if (!(hi > lo)) return true;
  const top = floorY + tallHeight;
  for (const o of blockers) {
    if (!o?.center || !o?.dimensions) continue;
    const bottom = o.center.y - (o.dimensions.y || 0) / 2;
    // Only what hangs in the band the filler would newly occupy.
    if (!(bottom < top - TALL_FILLER_INSET)) continue;
    if (o.center.y + (o.dimensions.y || 0) / 2 <= floorY + BASE_HEIGHT)
      continue;
    // …and only what is ON THIS WALL. Every upper in the room projects onto
    // this wall's axis, and one across the kitchen lands on the same params
    // while standing feet away: CB6F4B2C's `667FB3AB` sits 179" off this plane
    // and suppressed a tall filler that had clear air above it.
    const offPlane = Math.abs(
      (o.center.x - axes.start.x) * axes.wallNormal.x +
        (o.center.z - axes.start.z) * axes.wallNormal.z
    );
    if (offPlane > KITCHEN_CABINET_DIMENSIONS.depth) continue;
    const p =
      (o.center.x - axes.start.x) * axes.wallDir.x +
      (o.center.z - axes.start.z) * axes.wallDir.z;
    const half = (o.dimensions.x || 0) / 2;
    if (Math.min(p + half, hi) - Math.max(p - half, lo) > SPAN_EPS)
      return false;
  }
  return true;
}

/**
 * The tall unit a filler at `pieces[idx]` abuts, or null.
 *
 * Only the immediate neighbours are considered — the packer emits pieces in
 * order along the span, so a filler's neighbours are the pieces either side of
 * it. When it is sandwiched between two, the taller wins.
 *
 * A neighbour is tall four ways: a measured tall unit (a scanned pantry), a
 * FRIDGE (whose column the run builds — `fridgeColumnSpec`), a tall SPAN's
 * fixed module, or an AUTO bay a width-less tall override makes tall. The last
 * two are the same fact — the piece's governing override is tall — so both are
 * read through `governingOverrideForPiece`, the same derivation `emitBay`
 * builds the box from (RP27).
 */
function tallNeighbourOf(run, segIndex, pieces, idx, floorY, ctx) {
  let best = null;
  for (const p of [pieces[idx - 1], pieces[idx + 1]]) {
    if (!p) continue;
    let cand = null;
    if (p.kind === 'hard' && p.bay?.kind === 'tallUnit') {
      cand = { height: p.bay.height, depth: p.bay.depth };
    } else if (p.kind === 'hard' && p.bay?.kind === 'fridge') {
      cand = fridgeColumnSpec(run, floorY, p.bay, ctx);
    } else if (
      (p.kind === 'fixed' || p.kind === 'auto') &&
      isTallOverride(governingOverrideForPiece(run, segIndex, p))
    ) {
      // A tall span's module, or an auto bay the override made tall: the
      // filler beside it (its own remainder, or a scribe to the next thing)
      // matches it.
      cand = tallSpanSpec(floorY, ctx);
    }
    if (!cand || !(cand.height >= TALL_NEIGHBOUR_MIN_HEIGHT)) continue;
    if (!best || cand.height > best.height) best = cand;
  }
  return best;
}

/**
 * Fill one segment's fill zone [lo, hi] in ONE packer call: the hard-centred
 * pieces (appliance bays and floor-reaching openings) partition it, the free
 * spans between them pack flush, and every piece comes back with identity.
 * Cabinet / filler pieces are built here; a hard piece is only RECORDED —
 * its objects are the appliance's (built by buildApplianceObjects, or moved
 * to the solved centre by the caller) or an opening's (nothing to build).
 */
/**
 * The tall unit a filler should match.
 *
 * A filler carrying a TALL override is the span's OWN remainder (`pushFixed`
 * echoes the override onto it), and it matches the span unconditionally — the
 * way the span's modules beside it already do. The headroom gate below is for
 * the other case: a SCRIBE — beside a MEASURED pantry under a measured
 * over-fridge cabinet the run does not own and never rebuilds, beside a FRIDGE
 * column, or beside an auto bay a width-less tall override raised (RP27; such a
 * scribe carries no `overrideId`, so it stays headroom-gated). Applying it to
 * a span the user just placed produced a 34.5" "Cabinet Filler" wearing the
 * span's id, with the slab bridging onto it (RP25). A remainder that does
 * intersect a kept upper is a collision the pill reports, and "Rebuilt" is
 * the way out.
 */
function tallFillerFor(run, segIndex, wall, pieces, idx, floorY, state) {
  if (isTallOverride(pieces[idx]?.override))
    return tallSpanSpec(floorY, state.ctx);
  const tall = tallNeighbourOf(run, segIndex, pieces, idx, floorY, state.ctx);
  if (!tall) return null;
  const p = pieces[idx];
  const range = { start: p.start, end: p.end };
  return tallFillerClears(wall, range, tall.height, floorY, state.ctx)
    ? tall
    : null;
}

/**
 * What stands at each end of a segment's fill zone, in `fillerEnd.js`'s
 * vocabulary — the input to which end a sub-span's remainder filler takes.
 *
 * The zone was narrowed twice before it got here, and each narrowing names
 * its own bound: `segmentFillBounds` reserved the blind corner (`raw`), and
 * `endPieceBounds` then reserved the strip a terminal dishwasher's end panel
 * needs. What is left reaches the run's OWN drawn extent, and there the
 * question is whether the run dies into the wall or simply stops mid-wall —
 * a filler is invisible against the first and exposed at the second.
 *
 * Flush is `MIN_FILLER_IN` (¼"), borrowed rather than invented: it is already
 * the width under which `solveModuleSequence` emits no filler at all, so a
 * gap to the wall smaller than a filler can be is a zone that ends AT the
 * wall. Note a scan run's extent is the extent of the cabinetry RoomPlan
 * found, which routinely stops short of the corner — `runEndClosure` is what
 * widens those spans to the wall first, and this reads the widened bound.
 */
function fillZoneBounds(wall, seg, raw, lo, hi) {
  const wallLen = getWallAxes(wall)?.wallLen ?? 0;
  const segLo = Math.min(seg.startParam, seg.endParam);
  const segHi = Math.max(seg.startParam, seg.endParam);
  const flush = MIN_FILLER_IN * IN;
  const endOf = (zoneEdge, rawEdge, segEdge, wallEdge) => {
    if (Math.abs(zoneEdge - rawEdge) > SPAN_EPS) return 'dishwasher';
    if (Math.abs(rawEdge - segEdge) > SPAN_EPS) return 'corner';
    return Math.abs(segEdge - wallEdge) <= flush ? 'wall' : 'open-end';
  };
  return {
    lo: endOf(lo, raw.lo, segLo, 0),
    hi: endOf(hi, raw.hi, segHi, wallLen),
  };
}

/**
 * `bounds` names what stands at each end of the fill zone — `fillerEnd.js`'s
 * vocabulary. It carries the whole of what the old `{ openLo, openHi }` pair
 * said: an end the RUN's own drawn extent reaches is `wall` or `open-end`
 * (nothing beyond it either way), and anything else is a bound another pass
 * builds against, so `strandedFillerIndices` is derived from it rather than
 * passed alongside it.
 */
const OPEN_BOUNDS = new Set(['wall', 'open-end']);

/**
 * Does the run BUILD this bay's fridge column — the surround and its ½" side
 * panels? Only where it builds the install at all (not a scan run, which keeps
 * the column the scan measured), the fridge is built in (`fridgeIsBuiltIn`)
 * and there is a ceiling to run it to.
 */
function buildsFridgeColumnFor(reservation, ctx) {
  return (
    reservation.type === 'fridge' &&
    ctx.buildsFridgeColumn !== false &&
    fridgeIsBuiltIn(reservation.bay) &&
    typeof ctx.ceilingY === 'number'
  );
}

/** `ranges` minus every `cuts` interval, as sorted disjoint {lo, hi} pieces. */
function subtractRanges(ranges, cuts) {
  let out = ranges.map((r) => ({ ...r }));
  for (const c of cuts) {
    const next = [];
    for (const r of out) {
      if (c.hi <= r.lo || c.lo >= r.hi) {
        next.push(r);
        continue;
      }
      if (c.lo > r.lo) next.push({ ...r, hi: c.lo });
      if (c.hi < r.hi) next.push({ ...r, lo: c.hi });
    }
    out = next;
  }
  return out;
}

const FIT_IN = 0.0254;
/** Round to the nearest ¼" — the unit a cabinet is actually specified in. */
const asInches = (m) =>
  `${(Math.round((m / FIT_IN) * 4) / 4).toFixed(2).replace(/\.?0+$/, '')}"`;

/**
 * The fit warning's sentence, naming the widest cabinet that could not fit and
 * the gap it was asked to fit into.
 *
 * Falls back to the old general sentence when the packer reported no detail —
 * better a vague true sentence than a confident wrong one.
 */
function overflowMessage(details) {
  const worst = (details || [])
    .map((d) => ({ ...d, widest: Math.max(...(d.widths || [0])) }))
    .sort((a, b) => b.widest - a.widest)[0];
  if (
    !worst ||
    !Number.isFinite(worst.widest) ||
    !Number.isFinite(worst.span)
  ) {
    return "Fixed cabinet widths exceed the available space — they won't all fit.";
  }
  const many = (worst.widths || []).length > 1;
  return many
    ? `${asInches(worst.span)} of space here, but ${worst.widths.length} cabinets are fixed at ${worst.widths.map(asInches).join(' + ')} — they won't all fit.`
    : `A ${asInches(worst.widest)} cabinet is set where only ${asInches(worst.span)} fits.`;
}

/** Override ids named by a plan's overflowing stretches. */
const overflowIdsOf = (plan) =>
  new Set(plan.overflowDetails.flatMap((d) => d.overrideIds || []));

/**
 * The boxes in `pieces` the RUN sized that wear a front their line makes only
 * at other widths, as sequenced entries at the width it does make
 * (`bayWidths.kindFitWidthIn`): `{ anchorParam, width, override }`, the
 * override being the front's own record (a type-only override). Asked of the
 * box the fill would build — the run's body height and depth, its label.
 */
function lineFitEntries(pieces, run, segIndex, state, taken) {
  const out = [];
  for (const p of pieces) {
    if (p.kind !== 'auto') continue;
    const gov = governingOverride(
      run,
      segIndex,
      { start: p.start, end: p.end },
      null,
      'lower',
      taken
    );
    if (!gov?.type) continue;
    const widthIn = (p.end - p.start) / FIT_IN;
    const probe = {
      type: 'lower-cabinet',
      label: state.profile.lowerLabel,
      category: { 'lower-cabinet': true },
      dimensions: {
        x: p.end - p.start,
        y: runBodyHeight(run),
        z: segmentCabinetDepth(
          run.segments[segIndex],
          state.ctx,
          state.profile
        ),
      },
    };
    const fitIn = kindFitWidthIn(run, probe, widthIn, { front: gov.type });
    if (fitIn == null) continue;
    out.push({
      anchorParam: gov.anchorParam,
      width: fitIn * FIT_IN,
      override: gov,
    });
  }
  return out;
}

/**
 * The run's OWN fill obeys its line per kind (2026-10-21). A front chosen for
 * a bay the run sizes rides on whichever box the packer lays over its anchor,
 * and the packer chose that box from the family's BASE widths — Venus's run to
 * 42", its drawer bases only to 36". So a run that reflowed could quietly make
 * a 39" or 42" drawer base: a generic drawer stack on the plan, a part Venus
 * does not make in the estimate. Each such box is re-packed as a sequenced
 * entry at the width the line makes for its kind (the rule the drag, the panel
 * and the write boundary already ask, `bayWidths.js`), and the rest of the
 * stretch re-solves around it. A box whose fitted width would not fit its
 * stretch is left as the packer laid it, so no fit warning is invented for a
 * width nobody set. A family with no size catalog never gets here.
 */
function fitBaysToLine(plan, packed, pack, run, segIndex, state) {
  if (!familySizes(familyOf(run))) return plan;
  let fitted = [];
  let current = plan;
  // Each pass fixes what the last re-pack still left off the line; a few
  // passes settle any stretch (a fitted box never moves back off it).
  for (let pass = 0; pass < 3; pass += 1) {
    const more = lineFitEntries(
      current.pieces,
      run,
      segIndex,
      state,
      new Set(fitted.map((f) => f.override.id))
    );
    if (!more.length) break;
    let tried = [...fitted, ...more];
    let next = pack([...packed, ...tried]);
    const crowded = overflowIdsOf(next);
    if (tried.some((f) => crowded.has(f.override.id))) {
      tried = tried.filter((f) => !crowded.has(f.override.id));
      next = pack([...packed, ...tried]);
    }
    if (tried.length === fitted.length) break;
    fitted = tried;
    current = next;
  }
  if (!fitted.length) return current;
  // A fitted front governs its own box only: the auto boxes beside it are
  // told so, or the one its anchor now falls in would wear it too.
  const claimed = new Set(fitted.map((f) => f.override.id));
  return {
    ...current,
    pieces: current.pieces.map((p) =>
      p.kind === 'auto' ? { ...p, claimed } : p
    ),
  };
}

/** The fit warning's sentence for a hard piece the fill could not honour. */
function unsatisfiedMessage(u) {
  if (u.reason === 'overlap') {
    return `${u.kind} overlaps the appliance beside it by ${(u.delta / FIT_IN).toFixed(1)}".`;
  }
  if (u.reason === 'outside-run') {
    return `A ${asInches(u.width)} cabinet is set where this run has no space for it, so it was left out.`;
  }
  return `${u.kind} moved ${(u.delta / FIT_IN).toFixed(1)}" to keep clear of the corner cabinet or the end of the run.`;
}

/**
 * The width (metres) a sink base beside a grown corner reveal may step up to
 * — the line's next size above it (`nextSinkBaseWidthIn`) — or null. Never on
 * a SCAN run (a scanned bowl's base stays where post-processing sized it, and
 * a reprocess must not move it because a line sells a wider one — user ruling
 * 2026-10-22) nor a vanity, whose sizes are its own line's. A sink base's
 * width is its BAY's (sized from its bowl, `bayFootprint`): a width override
 * on its box changes nothing, so there is no user width here to defer to.
 */
function sinkBaseStepUp(run, piece) {
  if (run.source === 'scan' || profileOf(run).id === VANITY_PROFILE_ID)
    return null;
  const widthIn = Math.round(((piece.end - piece.start) / IN) * 1000) / 1000;
  const next = nextSinkBaseWidthIn(familyOf(run), widthIn);
  return next == null ? null : next * IN;
}

/**
 * What a zone PACKED AROUND, for a bay-edge drag to stop at
 * (`bayReach.js`): its two ends — named as `fillZoneBounds` names them (the
 * run's own end, a corner, an appliance end piece) — and every hard piece as
 * it stands AFTER the packer's clamp, a fridge column's side panels (`pad`)
 * included. Read off the fill itself rather than re-derived, so a drag cannot
 * grow a bay into space the fill would refuse it.
 */
function zoneRecord(segIndex, lo, hi, bounds, pieces, hard) {
  const padOf = new Map((hard || []).map((h) => [h.bay, h.pad || 0]));
  return {
    segIndex,
    lo,
    hi,
    ends: { lo: bounds.lo ?? null, hi: bounds.hi ?? null },
    pieces: pieces
      .filter((p) => p.kind === 'hard')
      .map((p) => {
        const pad = padOf.get(p.bay) || 0;
        return {
          start: p.start - pad,
          end: p.end + pad,
          kind: p.bay?.kind ?? null,
          bayId: p.bay?.bayId ?? null,
          ...(p.bay?.cabinetOverride
            ? { overrideId: p.bay.cabinetOverride.id }
            : {}),
        };
      }),
  };
}

function fillSegmentZone(
  wall,
  lo,
  hi,
  floorY,
  run,
  segIndex,
  state,
  hard,
  bounds = {},
  reveals = {}
) {
  // A bay the user placed by dragging its EDGE holds its position too, so it
  // rides with the appliances as a HARD piece: the span is partitioned at it
  // and the free stretch before it re-solves to the smaller gap, instead of
  // the bay being pushed along at whatever cursor the pieces before it reach.
  // That is the difference between "make it 24 inches" and "put its edge
  // here" — and it holds only while this zone still reaches around the bay
  // and nothing hard stands on it, which is the split `overridesForZone` makes.
  const {
    fixed,
    hard: positioned,
    displaced,
    stranded: unbuilt,
  } = overridesForZone(run, segIndex, lo, hi, SPAN_EPS, hard || []);
  // A placed bay whose centre this zone does not reach is built nowhere. Say
  // so: left out in silence, its stretch re-packed with the run's own widths
  // and a drawer base came back as plain doors, saved, with the drawer record
  // still in the document (`overridesForZone`).
  for (const o of unbuilt) {
    state.unsatisfied.push({
      bayId: o.id,
      kind: o.kind || 'lower-cabinet',
      segIndex,
      reason: 'outside-run',
      delta: Math.max(lo - o.anchorParam, o.anchorParam - hi),
      width: o.width,
    });
  }
  const pack = (widths) =>
    planSubSpanSequence(lo, hi, widths, [...(hard || []), ...positioned], {
      // The run's product family decides how the span's widths are chosen:
      // divided evenly (custom), the 3" grid (semi-custom), or its stock set.
      ...familyFillOpts(run),
      // …and these decide which END of each sub-span its remainder goes to —
      // a corner reveal's end before any other, since the remainder joins it
      // there (`cornerReveal.js`).
      loBound: reveals.lo ? 'reveal' : bounds.lo,
      hiBound: reveals.hi ? 'reveal' : bounds.hi,
    });
  // A placed bay something now stands on keeps its width only where that
  // still fits beside it. In a stretch that overflows it is left out and the
  // stretch re-solves with the run's own widths — an appliance pushed it
  // there, so the overflow is not one the user set (user, 2026-10-17).
  let packed = fixed;
  let plan = pack(packed);
  const crowded = new Set(
    plan.overflowDetails
      .flatMap((d) => d.overrideIds || [])
      .filter((id) => displaced.has(id))
  );
  if (crowded.size > 0) {
    packed = fixed.filter((f) => !crowded.has(f.override?.id));
    plan = pack(packed);
  }
  // A width-less record (a front, an appearance) lives with the box that wore
  // it last solve, not with the point it was first set at — a re-pack that
  // moved the seams would otherwise hand it to the neighbour
  // (`overrideHomes.js`). Every read of an override below is on this run.
  const homed = homedRun(
    run,
    segIndex,
    'lower',
    plan.pieces.filter((p) => p.kind === 'auto'),
    previousSpansOf(run, segIndex, 'lower', state.ctx?.existingObjects)
  );
  // …and a box the run sizes is held to the widths its line makes for its
  // kind (`fitBaysToLine`).
  plan = fitBaysToLine(plan, packed, pack, homed, segIndex, state);
  const { overflow, overflowDetails, unsatisfied } = plan;
  // Each corner reveal at this zone's ends takes in the leftover beside it,
  // so the leg has ONE filler at the corner, never 3" and another beside it.
  const padByBay = new Map((hard || []).map((h) => [h.bay, h.pad || 0]));
  const { pieces: revealed } = absorbLeftoverIntoReveals(
    plan.pieces,
    { lo, hi },
    {
      lo: reveals.lo ? { start: reveals.lo.lo, end: reveals.lo.hi } : null,
      hi: reveals.hi ? { start: reveals.hi.lo, end: reveals.hi.hi } : null,
    },
    (p) => padByBay.get(p.bay) || 0
  );
  // …and a sink base beside a reveal grown 3" or more past its minimum steps
  // up to the line's next size, into the reveal (`widenSinkBasesIntoReveals`).
  const { pieces, widened } = widenSinkBasesIntoReveals(
    revealed,
    (p) => sinkBaseStepUp(run, p),
    INSIDE_CORNER_REVEAL
  );
  for (const w of widened) state.widenedSinkBases.set(w.bayId, w.width);
  state.zones.push(zoneRecord(segIndex, lo, hi, bounds, pieces, hard));
  // A filler-only stretch between a doorway's keep-out and the run's open end
  // (or another keep-out, or a corner) is a stranded sliver, not a scribe — see
  // `strandedFillerIndices`. Skipped here, so no lower bay is recorded and the
  // upper and countertop passes, which follow `lowerBays`, skip it too.
  const stranded = strandedFillerIndices(pieces, {
    openLo: OPEN_BOUNDS.has(bounds.lo),
    openHi: OPEN_BOUNDS.has(bounds.hi),
    cornerLo: bounds.lo === 'corner',
    cornerHi: bounds.hi === 'corner',
  });
  if (overflow) {
    state.warnings.push({
      severity: 'warning',
      type: 'fit',
      // NAME the cabinet and the gap. The bare "widths exceed the available
      // space" is true and unactionable: the reader cannot tell which cabinet
      // or by how much, and the offending bay is packed flush somewhere else
      // entirely, so the drawing does not show them either. Measured case: a
      // 33" width set in a 12" slot beside an appliance.
      message: overflowMessage(overflowDetails),
    });
  }
  for (const u of unsatisfied) {
    state.unsatisfied.push({
      bayId: u.bay.bayId,
      kind: u.bay.kind,
      segIndex,
      reason: u.reason,
      delta: u.delta,
    });
  }
  for (let idx = 0; idx < pieces.length; idx += 1) {
    const p = pieces[idx];
    if (stranded.has(idx)) continue;
    if (p.kind === 'hard') {
      // A positioned CABINET is a hard piece that still has to be drawn —
      // every other hard piece is an appliance or an opening, whose geometry
      // belongs to something else. Emitted as a fixed bay so it is the same
      // cabinet it would have been sequenced, just seated where it was put.
      if (p.bay.cabinetOverride) {
        emitBay(
          wall,
          { start: p.start, end: p.end },
          floorY,
          homed,
          segIndex,
          state,
          'fixed',
          p.bay.cabinetOverride,
          null
        );
        continue;
      }
      state.lowerBays.push({
        segIndex,
        start: p.start,
        end: p.end,
        kind: p.bay.kind,
        bayId: p.bay.bayId,
      });
      // Openings and a single-sided corner unit are hard pieces but not bays
      // of the solved APPLIANCE table (nothing of theirs is moved to a bay).
      if (p.bay.kind !== 'opening' && !KEEP_OUT_BAY_KINDS.has(p.bay.kind)) {
        const slide = state.endPieceSlides.get(p.bay.bayId);
        state.bays.push({
          bayId: p.bay.bayId,
          kind: p.bay.kind,
          segIndex,
          start: p.start,
          end: p.end,
          center: (p.start + p.end) / 2,
          ...(slide != null ? { endPieceSlide: slide } : {}),
          // A sink base a corner reveal widened is built at its new width.
          ...(state.widenedSinkBases.has(p.bay.bayId)
            ? { width: state.widenedSinkBases.get(p.bay.bayId) }
            : {}),
        });
      }
      continue;
    }
    emitBay(
      wall,
      { start: p.start, end: p.end, claimed: p.claimed },
      floorY,
      homed,
      segIndex,
      state,
      // A box `fitBaysToLine` held to its line's widths packs as a fixed
      // entry, but its width is still the run's, not one the user set.
      p.kind === 'fixed' && !(p.override?.width > 0) ? 'auto' : p.kind,
      p.override || null,
      p.kind === 'filler'
        ? tallFillerFor(homed, segIndex, wall, pieces, idx, floorY, state)
        : null
    );
  }
}

/**
 * A vendor family's drawer bays drawn as the part it sells (item #23): Venus's
 * DB24 is three drawers with a short one on top, its DB24-2 two equal ones —
 * read off the part the estimate prices (`drawerStackFor`), so the drawing
 * cannot show four drawers on a cabinet priced as three. Every lower the fill
 * built passes here once, whichever path applied its front (a bay, a sink or
 * cooktop base, a generated kitchen through the engine). A generic family, and
 * a box the line makes no part for, keep the default stack. Mutates the
 * cabinets in place, like the appearance pass.
 */
function vendorDrawerStacks(cabinets, run, profileId) {
  const family = familyOf(run);
  if (!familySizes(family)) return;
  for (const cabinet of cabinets) {
    const columns = cabinet.columns || [];
    if (columns.length !== 1 || columns[0].type !== 'drawers') continue;
    const stack = drawerStackFor(family, cabinet, { profileId });
    if (!stack) continue;
    cabinet.columns = [
      {
        ...columns[0],
        drawerCount: stack.count,
        ...(stack.graduated ? { graduated: true } : {}),
      },
    ];
  }
}

/**
 * The upper band with the run's counter top on it (`counterTopY`): a wall
 * cabinet with drawers stands on the counter (`wallDrawers.js`).
 */
function withCounterTop(band, run, ctx) {
  if (!band) return band;
  return {
    ...band,
    counterTopY: (ctx?.floorY ?? 0) + counterTopOffsetFor(run),
  };
}

function fillRun(inputRun, inputCtx) {
  // The run's UPPER BAND, resolved once: every pass that lines up with the
  // uppers — a tall span, the fridge column, the corner upper L, the uppers
  // themselves — reads this one answer off the context (`upperBand.js`).
  const ctx = {
    ...inputCtx,
    upperBand: withCounterTop(
      upperBandFor(inputRun, inputCtx),
      inputRun,
      inputCtx
    ),
    // A vendor family's tall heights (`tallSpanSpec` snaps to them); absent
    // for every other family.
    tallHeightsIn: familyFillOpts(inputRun).tallHeightsIn,
  };
  // Links naming this run's bays or ends are hard anchors: apply them to the
  // bay anchors / segment ends BEFORE packing (layout-relationships §3.1
  // stage 2). A link that cannot be honoured is reported, never enforced.
  const anchored = applyLinkAnchors(
    { ...inputRun, bays: baysForRun(inputRun) },
    ctx.links || [],
    // `runEnds` is load-bearing and was being dropped here: an `align-end`
    // link whose target is ANOTHER run's end resolves through it
    // (linkAnchors.js), so without it every such link was refused `off-wall`
    // — on BOTH paths, including the browser re-solve that took the trouble
    // to build the map (`runEndsForRoom`) and hand it to this function.
    { openings: ctx.openings, walls: ctx.walls, runEnds: ctx.runEnds }
  );
  // The bays' sizes are the APPLIANCES' (bayFootprint.js): a swapped or
  // resized object is read HERE, so no stale width can be re-packed. A caller
  // that passes no objects keeps the stored sizes — and so does one that sets
  // `ctx.keepStoredBayFootprints` (the scan policy: those bays were derived
  // from these objects moments earlier and a run end's absorbed reveal makes
  // one deliberately wider than its body). Nothing else this pass sees is
  // withheld; only the re-derivation is skipped.
  const run = resolveRunBayFootprints(
    anchored.run,
    ctx.keepStoredBayFootprints ? [] : ctx.existingObjects
  );
  const floorY = ctx.floorY;
  // The run's dimension profile (kitchen | vanity) — resolved ONCE and read
  // by every emit in this fill, so a run cannot mix profiles mid-solve.
  const profile = profileOf(run);
  const state = {
    profile,
    cabinets: [],
    fillers: [],
    uppers: [],
    lowerBays: [], // { segIndex, start, end, kind, bayId? } per lower bay (hard ones included)
    bays: [], // { bayId, kind, segIndex, start, end, center, endPieceSlide?, width? } — the solved appliance table
    // bayId → width (metres) of each sink base a corner reveal widened
    // (`widenSinkBasesIntoReveals`).
    widenedSinkBases: new Map(),
    // bayId → how far a dishwasher's end piece slid that bay along its
    // segment (`endPieceBounds`), stamped on its solved-table entry.
    endPieceSlides: new Map(),
    unsatisfied: [...anchored.unsatisfied], // links + hard pieces that could not be honoured
    zones: [], // { segIndex, lo, hi, ends, pieces } per fill zone — see fillSegmentZone
    linksApplied: anchored.applied,
    upperBays: [], // { segIndex, start, end, cabinet } per emitted upper bay
    upperLCorners: new Map(), // cornerIndex → { reachA, reachB }: the upper Ls the upper tier planned
    counterHeightItems: [],
    warnings: [],
    bayIndex: 0,
    // Per-segment fill bounds AFTER any end-piece reservation, so the
    // end-piece pass fills the same strip the packer stayed out of.
    fillBounds: new Map(),
    ctx, // carries roomAppearance for effective-appearance resolution
  };
  const walls = [];
  const seenWall = new Set();

  // Plan each corner against the openings: a full corner cabinet, a blind
  // corner (one leg runs to the vertex, the other stops a cabinet-depth short),
  // or empty. cornerTrims hold the per-leg trim; cabinetCorners are the corners
  // that still get an L-corner cabinet, each with the L's leg (metres) — a
  // size the run's line sells, or the one the generator recorded.
  const cornerTrims = run.segments.map(() => ({ start: 0, end: 0 }));
  const cabinetCorners = new Map();
  // Single-sided corner units, one per blind corner whose extending leg can
  // host one: `{ segIndex, unit }`. Reserved on that leg before it is packed.
  const singleSided = [];
  // Oblique corners: `{ cornerIndex, stopA, stopB, kite }`, built after the
  // lower fill so the kite can be stamped beside the corner units.
  const oblique = [];
  // Bends through a drawn island leg that turn AWAY from the cabinets, with a
  // wedge between the fronts to fill (`outsideBend.js`).
  const outsideWedges = [];
  // Segment ends (`<segIndex>:start|end`) at a butt joint whose sliver beside
  // a dishwasher is the dishwasher's end piece, under a standalone cabinet
  // rather than under a fill cabinet (`endPieceBounds`).
  const sliverEnds = new Set();
  // What the LOWER tier decided at each corner, by corner index — the upper
  // tier plans its own corner against this (`upperTier.planUpperCorners`):
  // `{ mode: 'cabinet' | 'blind' | 'empty', extend? }`, or
  // `{ mode: 'oblique', symmetric }` with the bisector stop before any
  // body-clearing extension.
  const lowerCorners = new Map();
  // The same decisions as the panel reads them: what stands at each corner,
  // what the user asked for there, and which answers fit
  // (`lowerCornerTreatment.js`) — the lower tier's twin of `upperCorners`.
  // The reveals the corners asked for, built with their leg's fill.
  const pendingReveals = [];
  const revealAt = (i, edge) =>
    pendingReveals.find(
      (r) =>
        r.segIndex === i &&
        (Math.abs(r.lo - edge) <= SPAN_EPS || Math.abs(r.hi - edge) <= SPAN_EPS)
    ) || null;
  // A reveal built as a fixed 3" filler — where its leg is not filled at all,
  // or a dishwasher's end piece stands between it and the fill.
  const emitRevealAsIs = (r) =>
    emitBay(
      run.segments[r.segIndex].wall,
      { start: r.lo, end: r.hi },
      floorY,
      run,
      r.segIndex,
      state,
      'filler',
      null,
      null
    );
  // The reveal: a filler between the corner box and the first door on a leg
  // the box trims, so that door's handle clears the perpendicular face
  // (`INSIDE_CORNER_REVEAL`). The leg's fill starts past the filler.
  const emitCornerReveal = (leg, cornerIdx) => {
    const legEnd = leg === cornerIdx ? 'end' : 'start';
    const seg = run.segments[leg];
    const wall = seg?.wall;
    if (!wall) return;
    const vertex = legEnd === 'end' ? seg.endParam : seg.startParam;
    const other = legEnd === 'end' ? seg.startParam : seg.endParam;
    const away = Math.sign(other - vertex) || 1;
    const box =
      legEnd === 'end' ? cornerTrims[leg].end : cornerTrims[leg].start;
    const legLen = Math.abs(other - vertex);
    if (legLen < box + INSIDE_CORNER_REVEAL + SPAN_EPS) return;
    const from = vertex + away * box;
    const to = from + away * INSIDE_CORNER_REVEAL;
    // A hard piece already standing in the reveal (a manual run's range
    // parked 26" from the vertex) keeps its place; the corner then has
    // no reveal rather than a filler inside an appliance.
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    const taken = reservationsForSegment(run, leg).some(
      (r) =>
        Math.min(hi, r.centerParam + r.width / 2) -
          Math.max(lo, r.centerParam - r.width / 2) >
        SPAN_EPS
    );
    if (taken) return;
    if (legEnd === 'end') cornerTrims[leg].end = box + INSIDE_CORNER_REVEAL;
    else cornerTrims[leg].start = box + INSIDE_CORNER_REVEAL;
    // Built with the leg's fill, which it may grow into (`cornerReveal.js`);
    // the corner plan reads the 3" it claims from `cornerTrims` meanwhile.
    pendingReveals.push({ segIndex: leg, legEnd, box, lo, hi });
  };
  // An empty corner BOX is a void under a continuous counter: leg `x`'s slab
  // reaches the vertex (without this item it stopped a depth short), and the
  // other leg's starts one box in — one slab over the square, as at a corner
  // cabinet (`buildCornerCounterItems`).
  const spanCornerVoid = (x, xEnd, cornerIdx) => {
    const seg = run.segments[x];
    const wall = seg?.wall;
    if (!wall) return;
    const vertex = xEnd === 'end' ? seg.endParam : seg.startParam;
    const other = xEnd === 'end' ? seg.startParam : seg.endParam;
    const away = Math.sign(other - vertex) || 1;
    const far = vertex + away * cornerBoxFor(ctx);
    state.counterHeightItems.push({
      wallId: wall.id,
      range: { start: Math.min(vertex, far), end: Math.max(vertex, far) },
      type: 'lower-cabinet',
      id: `cornerBlindVoid_${run.id}_${cornerIdx}`,
    });
  };
  const lowerCornerReport = [];
  const wallIdsAt = (i) => [
    run.segments[i]?.wall?.id ?? null,
    run.segments[i + 1]?.wall?.id ?? null,
  ];
  for (let i = 0; i < run.segments.length - 1; i += 1) {
    if (!isCorner(run.segments[i], run.segments[i + 1])) continue;
    const anchored = ctx.anchoredAppliances === true;
    const nearA = nearestHardPieceToEnd(run, i, 'end', anchored);
    const nearB = nearestHardPieceToEnd(run, i + 1, 'start', anchored);
    const recorded = cornerTreatmentOverride(run, i);
    // Each leg's own row depth (`segmentCornerDepth`) — equal on every run but
    // a drawn island leg set deeper than the run it turns off. Legs of
    // DIFFERENT depths share no square box, so even a right angle is planned
    // the oblique way: each leg stops where its front meets the other's, and
    // a filler block takes the corner (angled-runs plan, Task 5).
    const depthA = segmentCornerDepth(run.segments[i], ctx);
    const depthB = segmentCornerDepth(run.segments[i + 1], ctx);
    const depthsDiffer = Math.abs(depthA - depthB) > SPAN_EPS;
    // A bend through a DRAWN island leg that turns away from the cabinets:
    // nothing collides, a wedge opens between the fronts. At 90° the longer
    // leg runs through to the outside corner (a NEGATIVE trim); otherwise
    // both run to the bend and a filler takes the wedge — bodyless under a
    // vendor line, like the butt joint at an angled wall corner (user,
    // 2026-11-03). Two walls at a convex room corner never reach this.
    if (
      (run.segments[i].wall.drawnFreeLeg ||
        run.segments[i + 1].wall.drawnFreeLeg) &&
      isOutsideBend(run.segments[i], run.segments[i + 1])
    ) {
      const plan = planOutsideBend({
        segA: run.segments[i],
        segB: run.segments[i + 1],
        depthA,
        depthB,
      });
      cornerTrims[i].end = plan.trimA;
      cornerTrims[i + 1].start = plan.trimB;
      lowerCorners.set(i, { mode: 'outside' });
      lowerCornerReport.push({
        cornerIndex: i,
        mode: 'outside',
        wallIds: wallIdsAt(i),
        requested: null,
        honoured: null,
        options: [],
      });
      if (plan.wedge && !familySizes(familyOf(run))) {
        outsideWedges.push({ plan, cornerIndex: i });
      }
      continue;
    }
    if (depthsDiffer || isObliqueCorner(run.segments[i], run.segments[i + 1])) {
      // A joint that is not a right angle: no L fits, no box is blind. Each
      // leg stops where its inner-room corner meets the other's on the
      // bisector (`obliqueStop`), and a kite fills the wedge — unless the
      // stretch between the stop and the leg's first hard piece is too
      // short to hold its own cabinet, in which case the stop extends to
      // that piece and the kite absorbs the sliver (the generator's
      // `cornerFillStops` rule). A recorded treatment says whether the
      // generator built the kite; the stops come from the geometry.
      //
      // The body a leg must clear is whatever stands at the vertex on the
      // OTHER leg: its cabinets, or an appliance parked there — a 30" fridge
      // reaches 6" further into the room than the cabinets, and the symmetric
      // stop packed 08E54F9E's galley into its side (2026-09-13).
      // Where each leg's front meets the other's: `depth / tan(θ/2)` for two
      // legs of one depth, the generator's two-depth stop otherwise.
      const stopGeomA = obliqueStop(
        run.segments[i],
        run.segments[i + 1],
        depthA,
        depthB
      );
      const stopGeomB = obliqueStop(
        run.segments[i],
        run.segments[i + 1],
        depthB,
        depthA
      );
      const symmetric = Math.max(stopGeomA, stopGeomB);
      const angle = cornerLegs(run.segments[i], run.segments[i + 1])?.angle;
      const clearing = (near, depth) => {
        if (!near.piece || angle == null) return 0;
        const fp = footprintForAppliance(near.piece.appliance);
        if (!(fp.depth > 0)) return 0;
        return legStopClearingBody(angle, depth, {
          near: near.d,
          far: near.d + (near.piece.hi - near.piece.lo),
          depth: fp.depth,
        });
      };
      const baseA = Math.max(stopGeomA, clearing(nearB, depthA));
      const baseB = Math.max(stopGeomB, clearing(nearA, depthB));
      // A vendor family makes no angled corner base (design 2026-10-05, D3),
      // so its oblique corner is the BUTT JOINT a contractor builds (user
      // ruling 2026-10-08): the legs' standard cabinets stop with their fronts
      // meeting on the bisector, the wedge behind them stays empty, and only
      // the corner's countertop is built over it.
      const bodyless = Boolean(familySizes(familyOf(run)));
      const sliver = (near, stop) =>
        near.d > stop &&
        near.d - stop < MIN_STANDALONE_CABINET_WIDTH - SPAN_EPS;
      // A butt joint has no kite to absorb the sliver. A dishwasher cannot
      // finish the leg itself — stopping at it faced its bare side onto the
      // empty wedge (19 Rapley Close under Venus) — so the leg keeps its stop
      // and the sliver is the dishwasher's END PIECE, whole (`endPieceBounds`).
      const endPieceSliver = (near, stop) =>
        bodyless &&
        sliver(near, stop) &&
        NEEDS_END_PANEL.has(near.piece?.bay?.kind);
      const extend = (near, stop) =>
        sliver(near, stop) && !endPieceSliver(near, stop) ? near.d : stop;
      const stopA = extend(nearA, baseA);
      const stopB = extend(nearB, baseB);
      if (endPieceSliver(nearA, baseA)) sliverEnds.add(`${i}:end`);
      if (endPieceSliver(nearB, baseB)) sliverEnds.add(`${i + 1}:start`);
      const stop = Math.max(baseA, baseB);
      cornerTrims[i].end = stopA;
      cornerTrims[i + 1].start = stopB;
      lowerCorners.set(i, { mode: 'oblique', symmetric });
      // An oblique joint has nothing to choose: its stops are the walls'.
      lowerCornerReport.push({
        cornerIndex: i,
        mode: 'oblique',
        wallIds: wallIdsAt(i),
        requested: null,
        honoured: null,
        options: [],
      });
      if (stop > 0) {
        oblique.push({
          cornerIndex: i,
          stopA,
          stopB,
          kite: recorded?.mode === 'oblique' ? recorded.kite !== false : true,
          // The butt joint (above): the slab over the wedge, no kite under it.
          bodyless,
        });
      }
      continue;
    }
    const farA = farFillBound(run.segments, i, 'end', cornerTrims);
    const farB = farFillBound(run.segments, i + 1, 'start', cornerTrims);
    // On the scan path the appliances are MEASURED objects nobody moves, so
    // the corner cabinet yields to them rather than sliding them (see
    // `cornerPlan`) — all but a sink, which gives the corner a little room
    // and is then moved to its bay (`sinkSlackToEnd`).
    const slackA = anchored
      ? sinkSlackToEnd(run, i, 'end', ctx, nearA.piece, farA)
      : 0;
    const slackB = anchored
      ? sinkSlackToEnd(run, i + 1, 'start', ctx, nearB.piece, farB)
      : 0;
    // On the generated path a sink base slides clear of the corner — unless
    // it has nothing to slide into (`pinnedSinkToEnd`).
    const pinA = anchored ? null : pinnedSinkToEnd(run, i, 'end', ctx, farA);
    const pinB = anchored
      ? null
      : pinnedSinkToEnd(run, i + 1, 'start', ctx, farB);
    const hard = {
      A: Math.min(nearA.d + slackA, pinA ? pinA.d : Infinity),
      B: Math.min(nearB.d + slackB, pinB ? pinB.d : Infinity),
      anchored,
      pinnedA: !!pinA,
      pinnedB: !!pinB,
    };
    // What the room allows here, graded on the numbers the plan itself reads,
    // and what the USER asked for. Their pick is honoured only while it fits;
    // the generator's record below is never graded at all
    // (`lowerCornerTreatment.js` says why).
    // WHICH piece sets a leg's hard distance, for the reason an option gives:
    // a pinned sink where it is the nearer, else the appliance measured above.
    const hardKind = (near, slack, pin) =>
      pin && pin.d <= near.d + slack ? 'sink' : (near.piece?.type ?? null);
    const lSet = cornerLSet(run);
    const measured = measureCorner(
      run.segments[i],
      run.segments[i + 1],
      ctx,
      hard,
      lSet
    );
    const options = gradeLowerCorner({
      ...measured,
      hardKindA: hardKind(nearA, slackA, pinA),
      hardKindB: hardKind(nearB, slackB, pinB),
    });
    const asked = userLowerTreatment(run, i);
    const requested = optionIdOf(asked);
    const honoured =
      !!requested && options.some((o) => o.id === requested && o.feasible);
    // A derived run carries the generator's decision for this corner
    // (`cornerTreatment.js`): honour it rather than grade the corner afresh,
    // or the first edit of a generated kitchen changes what stands in the box.
    // The user's own pick, while it fits, outranks it; with neither, the plan.
    const standing = honoured ? asked : recorded;
    const lapsedPick = () =>
      state.warnings.push({
        severity: 'warning',
        type: 'corner-treatment',
        cornerIndex: i,
        message:
          'The base corner chosen here no longer fits, so the layout decided it.',
      });
    // A leg that holds no cabinet — a doorway a few inches past the corner
    // box — makes this no corner at all where no L fits (`deadCornerLeg`): the
    // other leg runs straight to the wall with plain cabinets, and nothing
    // stands on this leg before its opening. Kept out of `lowerCorners`, so
    // the upper tier plans no corner here either and its uppers run straight
    // too; reported as the live leg taking the corner, which is what the
    // panel then says, so a person can still pick a blind unit or an empty
    // box here. Only the automatic plan: a pick that fits, or the generator's
    // record (never written for such a corner), decides it as before.
    const dead =
      !standing && !lFits(measured, measured.footprint)
        ? deadCornerLeg(run.segments[i], run.segments[i + 1], ctx)
        : null;
    if (dead) {
      lowerCornerReport.push({
        cornerIndex: i,
        mode: 'blind',
        extend: dead.leg === 'A' ? 'B' : 'A',
        straight: true,
        wallIds: wallIdsAt(i),
        requested,
        honoured: requested ? honoured : null,
        options,
      });
      if (requested && !honoured) lapsedPick();
      cornerTrims[i].end = dead.leg === 'A' ? dead.trim : 0;
      cornerTrims[i + 1].start = dead.leg === 'B' ? dead.trim : 0;
      continue;
    }
    // The SIZE of an L the user chose (`length` on their pick), graded before
    // the plan: an L's size IS the corner's trims. It is built only while
    // both legs take it — openings and appliances alike (`lFits`) — and
    // otherwise the line's own rule sizes the L, as for a blind unit.
    const askedL =
      honoured && standing?.mode === 'cabinet' && standing.length > 0
        ? standing.length
        : null;
    const askedLFits = askedL != null && lFits(measured, askedL);
    // An L with no size of its own: the user's pick takes the L the line
    // builds here (the size it was graded at); the generator's record is the
    // 36" every L was before a line could sell another — what it BUILT, which
    // a re-solve must not re-size.
    const planned =
      askedL != null && !askedLFits ? { mode: 'cabinet' } : standing;
    const plan =
      (planned &&
        planFromTreatment(
          planned,
          cornerBoxFor(ctx),
          honoured ? measured.footprint : CORNER_CABINET_FOOTPRINT
        )) ||
      cornerPlan(run.segments[i], run.segments[i + 1], ctx, hard, lSet);
    const cornerEntry = {
      cornerIndex: i,
      mode: plan.mode,
      extend: plan.extend,
      wallIds: wallIdsAt(i),
      requested,
      honoured: requested ? honoured : null,
      options,
    };
    lowerCornerReport.push(cornerEntry);
    if (requested && !honoured) lapsedPick();
    cornerTrims[i].end = plan.trimA;
    cornerTrims[i + 1].start = plan.trimB;
    lowerCorners.set(i, { mode: plan.mode, extend: plan.extend });
    if (plan.mode === 'cabinet') {
      cabinetCorners.set(i, plan.length);
      // Every L the line sells, graded on the plan's own numbers, for the
      // panel's Size row. A line with ONE L (every generic family's 36") has
      // nothing to choose and reports none.
      if ((lSet?.sizes || []).length > 1) {
        cornerEntry.size = {
          ...blindSizeReport(
            {
              continuous: false,
              sizes: [...lSet.sizes]
                .sort((a, b) => a - b)
                .map((size) => ({
                  size,
                  feasible: lFits(measured, size * INCHES_TO_METERS),
                })),
            },
            {
              built: plan.length,
              requested: askedL,
              honoured: askedL != null ? askedLFits : null,
              door: plan.length - cornerBoxFor(ctx),
            }
          ),
          shape: 'L',
        };
      }
      if (askedL != null && !askedLFits)
        state.warnings.push({
          severity: 'warning',
          type: 'corner-treatment',
          cornerIndex: i,
          message:
            'The corner cabinet size chosen here no longer fits, so the layout sized it.',
        });
    } else if (plan.mode === 'blind') {
      // The extending leg runs to the vertex. A plain door cabinet packed
      // into the corner box would face the other leg's side — so when the leg
      // has the room, the box is the blind half of a single-sided corner
      // unit, whose door sits on the accessible portion (the same unit the
      // generator seats when a sink crowds the perpendicular wall's corner).
      const x = plan.extend === 'A' ? i : i + 1;
      const xEnd = plan.extend === 'A' ? 'end' : 'start';
      // The other leg of this corner: the unit runs to the vertex and stands
      // end-on against that segment's wall, so it belongs to both.
      const cornerWallId =
        run.segments[plan.extend === 'A' ? i + 1 : i]?.wall?.id ?? null;
      // A recorded blind corner says whether a unit stood in the box — and
      // how long it was. A box the generator left blank stays blank, and a
      // unit it built is rebuilt at its own length rather than re-measured
      // against the leg's free run (which can read under 36" where the
      // generator, measuring from its reservations, seated one).
      //
      // The user's pick carries no `singleSided`: a unit wherever one fits.
      // Its `length`, when it carries one, is the SIZE they chose — graded
      // against the leg's free run like the pick itself, and built only while
      // it fits (the generator's length is never graded); without one, or
      // where it no longer fits, the family's rule sizes the unit. Every size
      // the family sells goes into the report, graded, for the panel.
      const wantsUnit = !standing || standing.singleSided !== false;
      let length = 0;
      if (wantsUnit) {
        const free = freeRunFromVertex(run, x, xEnd, ctx, cornerTrims);
        const set = cornerBlindSet(run);
        // `standing` is the user's pick only while it is honoured; otherwise
        // it is the generator's record, whose length is built, never graded.
        const askedLength =
          honoured && standing.length > 0 ? standing.length : null;
        const askedFits =
          askedLength != null &&
          fitsBlindSize(
            set,
            askedLength / INCHES_TO_METERS,
            free / INCHES_TO_METERS
          );
        length = askedFits
          ? askedLength
          : !honoured && standing?.length > 0
            ? standing.length
            : singleSidedLength(free, run);
        if (length > 0)
          cornerEntry.size = blindSizeReport(
            blindSizeChoices(set, free / INCHES_TO_METERS),
            {
              built: length,
              requested: askedLength,
              honoured: askedLength != null ? askedFits : null,
              door: length - cornerBoxFor(ctx),
            }
          );
        if (askedLength != null && !askedFits)
          state.warnings.push({
            severity: 'warning',
            type: 'corner-treatment',
            cornerIndex: i,
            message:
              'The blind corner size chosen here no longer fits, so the layout sized it.',
          });
      }
      const unit =
        length > 0
          ? buildSingleSidedCornerUnit({
              segment: run.segments[x],
              end: xEnd,
              length,
              depth: ctx.cabinetDepth,
              floorY,
              cornerIndex: i,
              runId: run.id,
              cornerWallId,
            })
          : null;
      if (unit) singleSided.push({ segIndex: x, unit });
      // A box that wanted a unit and got none — no blind base the line sells
      // fits the leg — is BLANK, exactly as a recorded blank box is: the
      // extending leg stops a box short too, and the counter spans the void.
      // It used to take the extending leg's ordinary fill instead, a cabinet
      // whose front faces the side of the other leg's run (user ruling
      // 2026-09-11, "blind unit if it fits, otherwise blank"; the generator's
      // `seatBlindCornerBox` has always done this, the engine only when the
      // generator had recorded it, 2026-10-20).
      const blank = !unit && (!!standing?.blank || wantsUnit);
      // Reported in the record's own words (`cornerTreatment.js`), so the
      // decision can be held against the generator's for the same corner.
      cornerEntry.singleSided = !!unit;
      cornerEntry.blank = blank;
      if (blank && !standing?.blank) {
        const box = cornerBoxFor(ctx);
        if (x === i) cornerTrims[i].end = box;
        else cornerTrims[i + 1].start = box;
      }
      // With a unit in the box only the trimmed leg needs a reveal — the
      // unit's side is the other face; a blank box trims both legs and both
      // get one.
      const revealLegs = unit ? [plan.extend === 'A' ? i + 1 : i] : [i, i + 1];
      for (const leg of revealLegs) emitCornerReveal(leg, i);
      // A blank box is a void under a continuous counter, not a counter
      // break (user ruling 2026-09-11: "blind unit if it fits, otherwise
      // blank" — with the countertop spanning it, as the generator's
      // `seatBlindCornerBox` has it).
      if (blank) spanCornerVoid(x, xEnd, i);
      state.warnings.push({
        severity: 'warning',
        type: 'corner',
        message: unit
          ? 'Blind corner — a single-sided corner cabinet runs to the corner, the other leg stops short (an opening or an appliance is near the corner).'
          : blank
            ? 'Blind corner — no blind corner cabinet fits, so the corner box is left empty under the countertop (an opening or an appliance is near the corner).'
            : 'Blind corner — one cabinet runs to the corner, the other stops short (an opening or an appliance is near the corner).',
      });
    } else if (
      honoured &&
      options.some((o) => o.mode === 'blind' && o.feasible)
    ) {
      // The plan only ever leaves a corner empty because something STANDS in
      // its box — a doorway, a fridge — and then the counter stops with the
      // cabinets. A person can choose it where the box is clear, and that is a
      // different thing: a void under a continuous counter, exactly the blank
      // box above. Both legs get their reveal; leg A's slab takes the square.
      // (A box something stands in is left open whoever chose it, and the
      // generator's own record is built as it always was.)
      emitCornerReveal(i, i);
      emitCornerReveal(i + 1, i);
      spanCornerVoid(i, 'end', i);
    } else {
      state.warnings.push({
        severity: 'warning',
        type: 'corner',
        message:
          'Corner left open — an opening or an appliance sits at the corner.',
      });
    }
  }

  // A dishwasher hard against a segment's fill bound would leave its side
  // unfinished with no room inside the run to close it. Reserve the end
  // piece BEFORE packing so the packer insets the appliance instead — the run
  // keeps the span the user set, and the piece lands inside it. (The
  // alternative, growing the run past `endParam`, silently changes a span the
  // user chose.) No reservation is made unless an appliance would actually
  // abut, so every other run packs exactly as before.
  // What the packer never moves on segment `i`: openings' keep-outs, a
  // single-sided corner unit, the run's keep-out bays.
  const immovableOn = (i) => [
    ...openingReservationsForSegment(run, i, ctx),
    ...adjacentOpeningReservationsForSegment(run, i, ctx),
    ...singleSided
      .filter((u) => u.segIndex === i)
      .map((u) => ({ lo: u.unit.range.start, hi: u.unit.range.end })),
    ...keepOutBays(run, i).map((b) => ({
      lo: b.anchorParam - b.width / 2,
      hi: b.anchorParam + b.width / 2,
    })),
  ];
  // The appliances that slide when a dishwasher at a bound is inset: the
  // dishwasher and every appliance butted against it in a row — the packer
  // places each at its own centre and only REPORTS an overlap, so a sink base
  // left standing put the inset dishwasher 4¼" inside it — and the free run
  // beyond the row they slide into, up to the next thing that stays put.
  const insetRow = (i, reservations, fromHi, lo, hi) => {
    const padOf = (r) =>
      buildsFridgeColumnFor(r, ctx) ? fridgeColumnPanels(run).thickness : 0;
    const order = [...reservations].sort((a, b) =>
      fromHi ? b.hi - a.hi : a.lo - b.lo
    );
    const row = [order[0]];
    let edge = fromHi
      ? order[0].lo - padOf(order[0])
      : order[0].hi + padOf(order[0]);
    for (const r of order.slice(1)) {
      const touches = fromHi
        ? r.hi + padOf(r) >= edge - SPAN_EPS
        : r.lo - padOf(r) <= edge + SPAN_EPS;
      if (!touches) break;
      row.push(r);
      edge = fromHi
        ? Math.min(edge, r.lo - padOf(r))
        : Math.max(edge, r.hi + padOf(r));
    }
    let limit = fromHi ? lo : hi;
    const stays = [
      ...order
        .filter((r) => !row.includes(r))
        .map((r) => ({ lo: r.lo - padOf(r), hi: r.hi + padOf(r) })),
      ...immovableOn(i),
    ];
    for (const o of stays) {
      if (fromHi && o.hi <= edge + SPAN_EPS) limit = Math.max(limit, o.hi);
      if (!fromHi && o.lo >= edge - SPAN_EPS) limit = Math.min(limit, o.lo);
    }
    return { row, room: Math.max(0, fromHi ? edge - limit : limit - edge) };
  };

  const endPieceBounds = (i, lo, hi) => {
    const reservations = reservationsForSegment(run, i);
    const slides = new Map();
    const spans = reservations
      .filter((r) => NEEDS_END_PANEL.has(r.bay?.kind))
      .map((r) => ({
        start: r.centerParam - r.width / 2,
        end: r.centerParam + r.width / 2,
      }));
    if (!spans.length) return { lo, hi, slides };
    // Every hard piece on the segment still has to fit after the reservation.
    // A 24" run holding a 24" dishwasher has no room for an end piece at all —
    // trimming it would clamp the appliance and emit two panels standing on
    // nothing.
    const hardWidth = reservations.reduce((sum, r) => sum + (r.width || 0), 0);
    // Insetting the appliance is NEVER done on a scan run.
    // `materializeScanRuns` re-emits cabinets but never appliance objects —
    // the run only RESERVES a measured appliance's footprint. Insetting here
    // would repack the cabinetry against a dishwasher the scan path leaves
    // exactly where it was measured, so the run's model and the emitted
    // objects would disagree. A scanned run still gets an end piece wherever
    // there is genuine room beside the appliance; it just never earns that
    // room by moving measured geometry.
    // Insetting only works where the appliance OBJECT follows the bay. On a
    // wall-backed segment `moveApplianceObjectsToBays` seats each appliance at
    // its solved param along `seg.wall`. A free-standing leg is filled on a
    // virtual wall of its own (`fillFreestandingSegment`), which hands its
    // slid bays back with that wall (`freeSlidBays`) for the same mover — without
    // that, the run's model moved while the dishwasher stood still and the
    // panel landed 4¼" inside the appliance it is supposed to close.
    const mayInset = run.source !== 'scan';
    // A strip beside the dishwasher that no cabinet will take is the end
    // piece's: under a fill cabinet anywhere, and under a standalone cabinet
    // in a butt joint's sliver (the corner loop's `sliverEnds`).
    const hiEnd = vertexOnHighSide(run.segments[i], 'end') ? 'end' : 'start';
    const loEnd = hiEnd === 'end' ? 'start' : 'end';
    // A bound the corner already CLOSES needs no piece: its L unit's leg, its
    // blind unit, or the reveal filler it stood on this leg (a blank box's) is
    // right there against the dishwasher. Reading such a bound as an open end
    // slid the row 4¼" off the filler for a second piece beside it whenever
    // the run had the room — so the same plan built differently as the corner
    // was decided (`runFillInvariants.nightly`, 18 layouts at B06B9943).
    const closedByCorner = (end, edge) => {
      const c = end === 'end' ? i : i - 1;
      if (c < 0 || c >= run.segments.length - 1) return false;
      if (lowerCorners.get(c)?.mode === 'cabinet') return true;
      if (singleSided.some((u) => u.unit.cornerIndex === c)) return true;
      // the corner's reveal, built later with this leg's fill
      if (revealAt(i, edge)) return true;
      return state.lowerBays.some(
        (b) =>
          b.segIndex === i &&
          b.kind === 'filler' &&
          (Math.abs(b.start - edge) <= SPAN_EPS ||
            Math.abs(b.end - edge) <= SPAN_EPS)
      );
    };
    const openHi = !closedByCorner(hiEnd, hi);
    const openLo = !closedByCorner(loEnd, lo);
    const leftoverMax = (end) =>
      sliverEnds.has(`${i}:${end}`)
        ? MIN_STANDALONE_CABINET_WIDTH
        : MIN_FILL_CABINET_WIDTH;
    const maxHi = leftoverMax(hiEnd);
    const maxLo = leftoverMax(loEnd);
    let outLo = lo;
    let outHi = hi;
    let insetLo = false;
    let insetHi = false;
    // The strip already standing between the dishwasher and each bound when
    // it is too narrow to be an end piece of its own (under the 2¼" minimum):
    // the ½" an island generated before 2026-10-20 left beside a ½" panel.
    // Where the row may move, that is the appliance against the bound — the
    // row slides until the piece is a real one.
    let stripLo = 0;
    let stripHi = 0;
    const tooNarrow = (gap) =>
      mayInset && gap < APPLIANCE_END_PANEL_MIN_WIDTH - SPAN_EPS;
    for (const sp of spans) {
      const gapHi = hi - sp.end;
      const gapLo = sp.start - lo;
      // Hard against the bound, or a strip too narrow to close it: earn the
      // room by insetting the appliance.
      if (gapHi <= SPAN_EPS || tooNarrow(gapHi)) {
        insetHi = mayInset && openHi;
        stripHi = Math.max(0, gapHi);
        // A LEFTOVER strip too small for a cabinet is the end piece's, not
        // the packer's. Without this the span packer closed it with an
        // anonymous filler — a ½"-wide "Cabinet Filler" beside an island's
        // dishwasher — and `planApplianceEndPanels` then found something
        // already standing against the appliance's edge and declined, so the
        // terminus was finished by a box that belongs to nothing. Moves
        // nothing, so it holds on a scan run too.
      } else if (gapHi < maxHi - SPAN_EPS) {
        outHi = Math.min(outHi, sp.end);
      }
      if (gapLo <= SPAN_EPS || tooNarrow(gapLo)) {
        insetLo = mayInset && openLo;
        stripLo = Math.max(0, gapLo);
      } else if (gapLo < maxLo - SPAN_EPS) {
        outLo = Math.max(outLo, sp.start);
      }
    }
    // The inset is the full end piece where the segment has room for it, and
    // otherwise what room it has down to the on-edge minimum (2x4 on edge + ¾"
    // panel) — the degrading the end-piece rule already states
    // (`applianceEndPanel.js`). Only asking for the full 4¼" refused 2.75"
    // of real room on a drawn run and left its dishwasher bare (819 Montrose,
    // 2026-10-20). Below the minimum there is no end piece to build, and the
    // end-panel pass says so.
    // The room is what the dishwasher's row can slide into (`insetRow`), on
    // top of the narrow strip already beside it. A row that cannot reach the
    // minimum keeps the strip, still the end piece's rather than the packer's.
    const inset = (fromHi, strip) => {
      const keepStrip = () => {
        if (fromHi) outHi = Math.min(outHi, hi - strip);
        else outLo = Math.max(outLo, lo + strip);
      };
      const { row, room } = insetRow(i, reservations, fromHi, outLo, outHi);
      if ([...slides.keys()].some((id) => row.some((r) => r.bayId === id)))
        return keepStrip();
      const piece = Math.min(APPLIANCE_END_PANEL_WIDTH, strip + room);
      if (piece < APPLIANCE_END_PANEL_MIN_WIDTH - SPAN_EPS) return keepStrip();
      const slide = piece - strip;
      for (const r of row) slides.set(r.bayId, fromHi ? -slide : slide);
      if (fromHi) outHi = Math.min(outHi, hi - piece);
      else outLo = Math.max(outLo, lo + piece);
    };
    if (insetHi) inset(true, stripHi);
    if (insetLo) inset(false, stripLo);
    if (outHi - outLo < hardWidth - SPAN_EPS)
      return { lo, hi, slides: new Map() };
    return { lo: outLo, hi: outHi, slides };
  };

  for (let i = 0; i < run.segments.length; i += 1) {
    const seg = run.segments[i];
    const { wall } = seg;
    if (!seenWall.has(wall.id)) {
      walls.push(wall);
      seenWall.add(wall.id);
    }

    // Corner-trimmed fill bounds for this segment (per the blind-corner plan).
    const raw = segmentFillBounds(run.segments, i, cornerTrims);
    // The corner reveals standing against this leg's fill, one per end.
    const revealsHere = pendingReveals.filter((r) => r.segIndex === i);
    const loReveal = revealsHere.find(
      (r) => Math.abs(r.hi - raw.lo) <= SPAN_EPS
    );
    const hiReveal = revealsHere.find(
      (r) => Math.abs(r.lo - raw.hi) <= SPAN_EPS
    );
    // Skip if the corner reservations consumed the whole span.
    if (raw.hi - raw.lo <= SPAN_EPS) {
      revealsHere.forEach(emitRevealAsIs);
      continue;
    }
    const { lo, hi, slides } = endPieceBounds(i, raw.lo, raw.hi);
    for (const [bayId, slide] of slides) state.endPieceSlides.set(bayId, slide);
    // A reveal takes the fill's leftover beside it only where the fill
    // reaches it — not across a dishwasher's end piece (`endPieceBounds`).
    const reveals = {
      lo: loReveal && Math.abs(lo - raw.lo) <= SPAN_EPS ? loReveal : null,
      hi: hiReveal && Math.abs(hi - raw.hi) <= SPAN_EPS ? hiReveal : null,
    };
    revealsHere
      .filter((r) => r !== reveals.lo && r !== reveals.hi)
      .forEach(emitRevealAsIs);
    // What stands at each end of the zone — read by the packer (which end a
    // filler goes to) and the scribe pass before the countertop.
    const ends = fillZoneBounds(wall, seg, raw, lo, hi);
    // The end-piece pass fills between this trimmed bound and the run's real
    // extent, so it must see the SAME bounds the packer used.
    state.fillBounds.set(i, { lo, hi, rawLo: raw.lo, rawHi: raw.hi, ends });

    // Appliance bays AND floor-reaching openings (doors / low windows) are
    // HARD-CENTRED pieces: they partition the segment where they stand and
    // the packer fills flush between them, so no base cabinet lands in a
    // doorway and a scanned range never slides. A piece that cannot stand
    // where it is (off the end, on top of another) is clamped and reported.
    // an opening's keep-out is clipped to the fill zone: it can start
    // before `lo` or run past `hi` and still only carve what is inside
    const openingKeepOuts = [
      ...openingReservationsForSegment(run, i, ctx),
      ...adjacentOpeningReservationsForSegment(run, i, ctx),
    ]
      .map((o) => ({ lo: Math.max(lo, o.lo), hi: Math.min(hi, o.hi), o }))
      .filter((c) => c.hi - c.lo > SPAN_EPS);
    const hard = [
      ...reservationsForSegment(run, i).map((r) => ({
        // …where the dishwasher's end piece slid it (`endPieceBounds`).
        center: r.centerParam + (slides.get(r.bayId) ?? 0),
        width: r.width,
        // A fridge's neighbours pack against its SURROUND, not its box: where
        // the run builds the install (`buildFridgeInstall` → `buildFridgeSurround`,
        // a ceiling to run it to), ½" side panels stand either side of the
        // fridge, and a neighbour packed to the fridge's own width sat half
        // an inch inside each. The bay stays the fridge's; the strips are
        // kept clear (`spanPacking` `pad`). The generator reserves the same
        // footprint (`occupiedRangesByWall`). A scan run keeps the column the
        // scan measured and pads nothing.
        pad: buildsFridgeColumnFor(r, ctx)
          ? fridgeColumnPanels(run).thickness
          : 0,
        // …and the column SEATS at a wall end: its panel clear of the wall,
        // the stretch's remainder on the WALL side of it rather than between
        // the fridge and the cabinets (`spanPacking.seatColumnAtEnd`). The
        // same gate as the pad — a scan run's fridge stays where it was
        // measured.
        seats: buildsFridgeColumnFor(r, ctx),
        // What the fridge ITSELF may not stand in front of — a window whose
        // sill is below its top. The lower fill never sees those (a base
        // cabinet passes under), so the packer has to be told.
        ...(buildsFridgeColumnFor(r, ctx)
          ? {
              avoid: blockingOpeningRangesForAppliance(run, i, r.type, ctx),
              wallFiller: FRIDGE_WALL_FILLER_MIN,
            }
          : {}),
        bay: {
          bayId: r.bayId,
          kind: r.bay.kind,
          // Carried so a filler landing beside a TALL unit can match it — see
          // `tallNeighbourOf`. Only the anchored tall units have these.
          ...(r.bay.height != null ? { height: r.bay.height } : {}),
          ...(r.bay.depth != null ? { depth: r.bay.depth } : {}),
          // …and so a filler beside a FRIDGE can measure the column the SCAN
          // put there, which no dimension on the bay records
          // (`fridgeColumnSpec` → `scannedColumnFor`).
          ...(r.bay.applianceId != null
            ? { applianceId: r.bay.applianceId }
            : {}),
        },
      })),
      // The single-sided corner unit at this leg's vertex, if it has one:
      // hard-centred so the packer fills flush beside it, recorded as a lower
      // bay so an upper mirrors it, but no appliance — see fillSegmentZone.
      ...singleSided
        .filter((s) => s.segIndex === i)
        .map((s) => ({
          center: (s.unit.range.start + s.unit.range.end) / 2,
          width: s.unit.range.end - s.unit.range.start,
          bay: { bayId: s.unit.id, kind: 'corner' },
        })),
      ...openingKeepOuts.map((c) => ({
        center: (c.lo + c.hi) / 2,
        width: c.hi - c.lo,
        bay: {
          bayId: `opening_${c.o.openingId || 'adjacent'}`,
          kind: 'opening',
        },
      })),
      // The run's own keep-out bays — a corner body it does not own, floor
      // the layout holds open (a walkway, the island's aisle, a wall solid,
      // an opening's zone). Clipped to the fill zone and to what the opening
      // keep-outs above do not already cover, so two hard pieces never
      // overlap and the packer reports nothing. Until 2026-09-13 a stored
      // run's `corner` bays reached nothing here at all.
      ...keepOutBays(run, i).flatMap((b) =>
        subtractRanges(
          [
            {
              lo: Math.max(lo, b.anchorParam - b.width / 2),
              hi: Math.min(hi, b.anchorParam + b.width / 2),
            },
          ],
          openingKeepOuts
        )
          .filter((c) => c.hi - c.lo > SPAN_EPS)
          .map((c) => ({
            center: (c.lo + c.hi) / 2,
            width: c.hi - c.lo,
            bay: { bayId: b.id, kind: b.kind },
          }))
      ),
    ];
    // A fill bound that is the run's own drawn extent has nothing beyond it;
    // a trimmed one (corner reservation, dishwasher end piece) is built
    // against by another pass, so a filler there is a scribe, not a sliver.
    // `cornerTrims` keeps the 3" alone: what a reveal takes in beyond it is
    // the leg's own stretch, which the upper tier mirrors as a filler and a
    // dragged upper may still claim (a place held past the zone it was put in
    // is kept as a width only, `overridesForZone`). The upper's corner strip
    // over the 3" joins that filler (`upperTier.absorbSlivers`).
    fillSegmentZone(wall, lo, hi, floorY, run, i, state, hard, ends, reveals);
  }

  // Parallel UPPER pass on the SAME wall-backed segments. An explicit stored
  // run.uppers wins; unset falls to the run's PROFILE default (kitchen ON,
  // vanity OFF — a bathroom wants a mirror over the counter, not boxes).
  // Uppers skip every opening on the wall plus the sink/range/fridge; the
  // dishwasher gets one above it.
  if (uppersEnabled(run)) {
    // Tagged in EMIT order and before the lone-filler drop, so a dropped
    // filler still consumes its bay index exactly as it always has.
    const emitted = fillUppers(
      run,
      ctx,
      { cornerTrims, cabinetCorners, corners: lowerCorners },
      state.lowerBays
    );
    state.upperLCorners = emitted.lCorners;
    state.upperCorners = emitted.corners;
    state.warnings.push(...emitted.warnings);
    for (const cabinet of emitted.uppers) {
      tagBay(cabinet, run.id, state.bayIndex);
      state.bayIndex += 1;
    }
    const kept = dropLoneFillerUppers(emitted);
    state.uppers.push(...kept.uppers);
    state.upperBays.push(...kept.upperBays);
  }

  // Appliance objects (sink bases join the cabinets; DW/sink add counter items).
  // The fridge unit (surround + over-fridge cabinet) tops out at the run's
  // upper-cabinet line so it lines up with the uppers — pass that top through
  // ctx. Uppers OFF is the "no upper line" case the install falls back to the
  // ceiling for.
  //
  // Uppers ON but none emitted is NOT that case: the run has an upper line, it
  // just has no bay to hang one on (a run of nothing but its fridge). Measuring
  // the emitted uppers made it read as one, which put the column at the ceiling
  // while `fridgeColumnSpec` — deciding a scribe's height during the LOWER
  // pass, before any upper exists — put the filler beside it on the line. Two
  // derivations of one number, disagreeing by two feet. The line is the same
  // band top an emitted upper tops out at, so this changes nothing
  // wherever one stands (all 3 corpus fridge runs), and agrees with the scribe
  // where none does.
  //
  // Both lines are now read off the BAND (plan 2026-10-21, C22): the hood's
  // is the uppers' own line, the column's the stacked row's top where there
  // is one (`fridgeColumnLineY`). Today the band's top is the line every
  // emitted upper tops out at, so nothing moves without a stack — and with
  // one, a max over the emitted boxes would have taken the hood up with it.
  const upperTopY = !uppersEnabled(run) ? null : ctx.upperBand.topY;
  const columnLineY = !uppersEnabled(run)
    ? null
    : fridgeColumnLineY(run, ctx.upperBand, floorY);
  // Appliance objects are built at the bay's SOLVED centre (the packer may
  // have clamped a bay that stood off the end of a shortened wall), not at
  // the stored anchor.
  // …and at its solved WIDTH where the fill changed it (a sink base a corner
  // reveal widened).
  const solved = new Map(state.bays.map((b) => [b.bayId, b]));
  const runForAppliances = {
    ...run,
    bays: baysForRun(run).map((b) => {
      const at = solved.get(b.id);
      if (!at) return b;
      return {
        ...b,
        anchorParam: at.center,
        ...(at.width != null ? { width: at.width } : {}),
      };
    }),
  };
  const appl = buildApplianceObjects(
    runForAppliances,
    upperTopY != null
      ? {
          ...ctx,
          upperTopY,
          columnTopY: columnLineY,
          hoodChimneyTopY: hoodChimneyTopY(ctx.upperBand),
        }
      : ctx
  );
  state.cabinets.push(...appl.sinkBases, ...appl.cooktopBases);
  state.counterHeightItems.push(...appl.counterItems);

  // A TALL appliance (fridge) parked at a corner can block a doorway / opening
  // on the adjoining wall — warn so the user moves it clear (base cabinets are
  // exempt; they sit below the swing).
  // A fixed-size install piece that no longer matches its appliance — a 36"
  // hood left over a 48" range. The run may not resize a chosen product
  // (`mayResizeObject`) or override a width the user set (`ownSizeAxes`), so
  // say so rather than stretch it.
  for (const m of appl.installMismatches || []) {
    const sizes = `The ${m.label} is ${(m.own / 0.0254).toFixed(0)}" and its ${m.applianceLabel} is ${(m.wanted / 0.0254).toFixed(0)}"`;
    state.warnings.push({
      severity: 'warning',
      type: 'fit',
      message: m.userSized
        ? `${sizes} — you set its width; resize it to match.`
        : `${sizes} — pick a matching product or the placeholder to have it built to size.`,
    });
  }

  // A wood hood on a run whose line sells none — the run left Venus — is the
  // built metal hood again (`reuseInstallPiece`); the shell and its insert
  // leave the estimate, so say why.
  for (const sku of appl.droppedWoodHoods || []) {
    state.warnings.push({
      severity: 'warning',
      type: 'fit',
      message: `${familyOf(run).name} sells no wood hood, so the ${sku} over the range is a metal hood again`,
    });
  }

  // A floor-standing appliance bay that now stands ACROSS a window on its own
  // wall. Widening a hard bay (a swap to a wider model) never re-tests the
  // wall's openings, and the generator's placement-time check is not reachable
  // from here — so report it; never slide an appliance the user did not ask to
  // move (#1754 follow-up, user-found 2026-09-05).
  for (const c of applianceOpeningOverlaps(run, ctx, state.bays)) {
    state.warnings.push({
      severity: 'warning',
      type: 'clearance',
      bayId: c.bayId,
      message: `The ${c.kind} stands across ${(c.overlap / 0.0254).toFixed(0)}" of a window on this wall — it rises above the sill.`,
    });
  }

  for (const c of tallItemOpeningConflicts(appl.objects, ctx)) {
    state.warnings.push({
      severity: 'warning',
      type: 'clearance',
      message: `${c.object.label || c.object.type} blocks the clear space in front of a ${c.openingKind} on an adjacent wall — move it clear.`,
    });
  }

  // Build the corner cabinets up front so the corner-fill counter items can
  // reference them: a corner unit isn't an auto bay, but it IS the real object
  // beneath the corner countertop, so the leg countertop's `parents` should
  // point at it — not at a synthetic, unresolvable placeholder.
  const kites = oblique
    .filter((o) => o.kite)
    .map((o) => {
      const kite = buildObliqueKite({ run, ...o, floorY, ctx });
      // the butt joint: the slab over the wedge, no cabinet under it
      return kite && o.bodyless ? { ...kite, cabinet: null } : kite;
    })
    .filter(Boolean);
  const cornerUnits = [
    ...buildCornerUnits(run, floorY, cabinetCorners, ctx),
    ...singleSided.map((s) => s.unit),
    ...kites.map((k) => k.cabinet).filter(Boolean),
    ...outsideWedges
      .map(({ plan, cornerIndex }) =>
        buildOutsideWedgeBody({
          plan,
          run,
          cornerIndex,
          floorY,
          height: runBodyHeight(run),
        })
      )
      .filter(Boolean),
  ];
  // The leg slab spans a single-sided unit like any lower bay (an L-corner's
  // counter items come from buildCornerCounterItems below).
  for (const { segIndex, unit } of singleSided) {
    state.counterHeightItems.push({
      wallId: run.segments[segIndex].wall.id,
      range: unit.range,
      type: 'lower-cabinet',
      id: unit.id,
      depth: unit.dimensions.z,
    });
  }
  // Matching L-corner UPPER at each cabinet corner (when uppers are on) so the
  // corner gets an upper just like the straight legs. Pushed into state.uppers
  // so it picks up the run/room appearance alongside the rest of the uppers.
  // The STACKED row (`upperStack.js`), over the straight uppers and corner
  // L's just built — never over what the lone-filler drop took away. It is
  // the run's like the uppers it stands on, and wears their look below.
  const stackRow = [];
  if (uppersEnabled(run)) {
    const cornerUppers = buildCornerUppers(
      run,
      floorY,
      state.upperLCorners,
      ctx.upperBand
    );
    state.uppers.push(...cornerUppers);
    const stacked = fillStackRow(run, ctx, {
      built: state.upperBays,
      lCorners: state.upperLCorners,
      cornerUppers,
    });
    for (const s of stacked.stack) {
      s.cabinet.addedBy = 'manual-cabinet-run';
      s.cabinet.runId = run.id;
      state.uppers.push(s.cabinet);
      stackRow.push(s);
    }
    state.warnings.push(...stacked.warnings);
  }

  // Corner-fill counter items so the leg countertops extend over the corner
  // cabinet (the two leg slabs overlap in the corner box).
  state.counterHeightItems.push(
    ...buildCornerCounterItems(run, cabinetCorners, cornerUnits, ctx)
  );

  // A run may not TERMINATE on an appliance. A dishwasher continues a run but
  // is not cabinetry — bare sides, and no countertop of its own — so a run
  // stopping on one dead-ends its slab on the appliance and leaves the raw
  // side facing the room. Emitted as a FILLER BAY (a lower-cabinet, like every
  // other filler) rather than a blank panel: that makes it run-owned, puts it
  // in `counterHeightItems` so the slab below spans it, and gets it replaced
  // cleanly on the next re-solve — the merge keeps only what the policy keeps
  // (`keepRule` in `mergeResolvedRuns.js`: kept-mode uppers and the oven
  // column base), so everything else a run owns is re-emitted or gone. A
  // blank panel would be none of those and would orphan exactly the way the
  // generator's did.
  //
  // Runs after every other body is placed, so "nothing abuts this edge" is
  // final, and before the countertop pass so the slab covers it.
  for (let i = 0; i < run.segments.length; i += 1) {
    const { wall } = run.segments[i];
    const bounds = state.fillBounds.get(i);
    if (!bounds) continue;
    const { lo, hi, rawLo, rawHi } = bounds;
    if (hi - lo <= SPAN_EPS) continue;
    const wallLen = getWallAxes(wall)?.wallLen ?? 0;
    if (!wallLen) continue;

    const standing = state.counterHeightItems
      .filter((c) => c.wallId === wall.id && c.range)
      .map((c) => c.range);
    // The run's REAL extent bounds the piece. Without these an appliance
    // abutting an untrimmed bound would put its end piece past `endParam` —
    // growing a span the user set, which is the option we rejected. When no
    // reservation was made these leave zero room and nothing is emitted.
    const extentLo = { start: -wallLen, end: rawLo };
    const extentHi = { start: rawHi, end: 2 * wallLen };
    const bodies = [...standing, extentLo, extentHi];
    const appliances = state.counterHeightItems
      .filter((c) => c.wallId === wall.id && c.range && c.type === 'dishwasher')
      // The appliance's OBJECT id rides along: the panel is that appliance's
      // install piece, and `installFor` is how every consumer knows it.
      .map((c) => ({ type: 'dishwasher', range: c.range, id: c.id }));
    if (!appliances.length) continue;

    const openingRanges = allOpeningReservationsForSegment(run, i, ctx);

    // Outside the segment's fill bounds the run simply does not reach — that
    // wall is not a cabinetry zone, however much of it there is. Passing it as
    // blocked is what stops a dishwasher at a run end being read as "244" of
    // free span, put a cabinet there".
    const blockedRanges = [
      { start: -wallLen, end: lo },
      { start: hi, end: 2 * wallLen },
      ...openingRanges.map((r) => ({ start: r.lo, end: r.hi })),
    ];

    // The strip `endPieceBounds` kept back beside a dishwasher at a bound is
    // that dishwasher's end piece, WHOLE. Handed to `planApplianceEndPanels`
    // it reads as a reservation and is cut to 4¼" — leaving the rest of a 5"
    // leftover strip, or of a butt joint's sliver, open beside the piece. An
    // opening inside the strip leaves it to that rule.
    const clearOfOpenings = (seg) =>
      !openingRanges.some(
        (r) => r.lo < seg.end - SPAN_EPS && r.hi > seg.start + SPAN_EPS
      );
    // Under the narrowest filler it is a scribe, as any leftover is.
    const keptBackPiece = (seg) =>
      seg.end - seg.start >= MIN_FILLER_IN * IN - SPAN_EPS &&
      clearOfOpenings(seg);
    const keptBack = [];
    for (const a of appliances) {
      const high = { start: hi, end: rawHi, applianceId: a.id };
      const low = { start: rawLo, end: lo, applianceId: a.id };
      if (
        rawHi - hi > SPAN_EPS &&
        Math.abs(a.range.end - hi) < SPAN_EPS &&
        keptBackPiece(high)
      )
        keptBack.push(high);
      if (
        lo - rawLo > SPAN_EPS &&
        Math.abs(a.range.start - lo) < SPAN_EPS &&
        keptBackPiece(low)
      )
        keptBack.push(low);
    }
    const endSegments = [
      ...keptBack,
      ...planApplianceEndPanels({
        wallLen,
        bodies: [...bodies, ...keptBack],
        blockedRanges,
        appliances,
      }),
    ];
    // A terminus the run could not finish — the appliances fill the segment
    // with nothing left for a panel, and there is no honest way to invent the
    // room: growing the run changes a span the user set, insetting moves an
    // appliance the scan measured. Say so instead of leaving a bare
    // dishwasher side to be discovered in the 3D view. The run's own extent
    // finishes nothing at a FREE end — only where it joins the next segment,
    // whose corner treatment stands there. Counting it everywhere is why this
    // warning never fired for the case it was written for.
    // …or by something that is not this run's at all: another run's cabinet
    // across a corner, a scanned storage unit standing against it. Probed an
    // inch past the side at mid-depth, against the room's other floor-standing
    // bodies — the run sees only its own, and read those sides as open.
    const axes = getWallAxes(wall);
    const others = (ctx.existingObjects || []).filter(
      (o) =>
        o?.center &&
        o?.dimensions &&
        o.runId !== run.id &&
        !appliances.some((a) => a.id === o.id) &&
        o.type !== 'upper-cabinet' &&
        !o.category?.['upper-cabinet'] &&
        CLOSES_A_SIDE.test(o.type || '')
    );
    const closedByOther = (w, param) => {
      if (!axes || !others.length) return false;
      const depth = segmentCornerDepth(run.segments[i], ctx) / 2;
      const p = {
        x: axes.start.x + param * axes.wallDir.x + depth * axes.wallNormal.x,
        z: axes.start.z + param * axes.wallDir.z + depth * axes.wallNormal.z,
      };
      return others.some((o) =>
        isPointInsidePolygon(
          p,
          footprintFor(
            o.center,
            o.dimensions.x,
            o.dimensions.z,
            o.rotation?.y || 0
          )
        )
      );
    };
    const seg = run.segments[i];
    const joins = (end) =>
      end === 'start' ? i > 0 : i < run.segments.length - 1;
    const hiEnd = vertexOnHighSide(seg, 'end') ? 'end' : 'start';
    // Every appliance closes the side of a dishwasher beside it, where it was
    // solved — a range or a fridge carries no counter item, but its side is
    // finished. A fridge stands as its COLUMN, surround panels included, as
    // the packer packed against it.
    const appliancesStanding = reservationsForSegment(runForAppliances, i).map(
      (r) => {
        const pad = buildsFridgeColumnFor(r, ctx)
          ? fridgeColumnPanels(run).thickness
          : 0;
        return { start: r.lo - pad, end: r.hi + pad };
      }
    );
    const finished = [
      ...standing,
      ...appliancesStanding,
      ...(joins(hiEnd === 'end' ? 'start' : 'end') ? [extentLo] : []),
      ...(joins(hiEnd) ? [extentHi] : []),
      ...endSegments,
    ];
    for (const a of appliances) {
      for (const side of [-1, 1]) {
        const edge = side < 0 ? a.range.start : a.range.end;
        const gapTo = (r) => (side < 0 ? edge - r.end : r.start - edge);
        if (edge <= SPAN_EPS || edge >= wallLen - SPAN_EPS) continue;
        // Something on the far side of this edge, within a scribe of it (a
        // leftover under the narrowest filler is scribed, never built). The
        // appliance's own range touches the edge from the NEAR side, and is
        // not what closes it.
        const abuts = finished.some((r) => {
          const gap = gapTo(r);
          return gap > -SPAN_EPS && gap < MIN_FILLER_IN * IN - SPAN_EPS;
        });
        if (abuts) continue;
        if (closedByOther(wall, edge + side * IN)) continue;
        state.warnings.push({
          severity: 'warning',
          // A capacity problem, like every other `fit` warning: what stands on
          // the run leaves no span for the piece that finishes it.
          type: 'fit',
          message:
            'A dishwasher ends this run with no room for a finished end panel — the run is too short for what stands on it.',
        });
      }
    }
    for (const segment of endSegments) {
      // A finished PANEL, not a cabinet. The module's own contract ("emitted
      // as a normal `blank-panel` through `buildBlankPanel`") and the
      // generator's copy of this pass both say so; the run had drifted to a
      // filler-typed lower cabinet, which is why an island's dishwasher was
      // closed by a ½"-wide "Cabinet Filler" that belonged to nothing.
      const panel = buildBlankPanel({
        wall,
        segment,
        floorY,
        fullDepth: true,
        filler: true,
        // As deep as the bays beside it (`emitBay` builds them at this).
        depth: segmentCabinetDepth(run.segments[i], ctx, profile),
      });
      if (!panel) continue;
      panel.label = 'Dishwasher End Panel';
      panel.category = { ...(panel.category || {}), dwEndPanel: true };
      // Cut from the run's stock, so it wears the run's look like the filler
      // bays beside it — a vendor line prices it by that colour within that
      // door design (`vendorSizes.colourCodeFor`); without them a Venus
      // dishwasher return had no price. Drawn flat, as every filler is.
      applyAppearanceBundle(panel, effectiveAppearance({ run, ctx }));
      panel.doorStyle = 'flat';
      // Its appliance — what makes it an INSTALL piece rather than cabinetry
      // (`applianceInstall.js`): the run rebuilds it, sweeps a stale one, and
      // carries it when the appliance's bay slides.
      if (segment.applianceId) panel.installFor = segment.applianceId;
      tagBay(panel, run.id, state.bayIndex);
      state.bayIndex += 1;
      state.fillers.push(panel);
      state.counterHeightItems.push({
        wallId: wall.id,
        range: segment,
        type: 'lower-cabinet',
        id: panel.id,
        ...(panel.dimensions?.z != null ? { depth: panel.dimensions.z } : {}),
      });
    }
  }

  // The vanity counter's front edge, and the bowl that hangs off it, both
  // need these — resolved once, here, where the slab is built.
  const isVanityProfile = profile.id === VANITY_PROFILE_ID;
  const runBodyDepth = ctx.cabinetDepth ?? profile.depth;
  // A run whose recorded overhang still describes its body reproduces the
  // counter that is actually there — 26% of vanities are granted no front
  // strip at all, a toilet standing in it (`vanityToRun`). Otherwise the
  // profile's own, grown to fit a bowl where the carcass is shallow.
  //
  // `standsAtMeasuredDepth`, NOT the sizing intent, and `sizing.js` carries
  // the distinction: the granted strip is an OBSERVATION whichever way the
  // run is sized, because a BUILT vanity's counter is granted or refused it by
  // the same rule the scan path uses. Asking the intent here sent a
  // standard-sized generated vanity back to the fit rule and pushed its
  // counter into the strip its room had refused. What the answer must not be
  // is the field's mere PRESENCE: a run standing at a standard depth while
  // still carrying the overhang measured against a carcass it no longer has
  // would build the scan's envelope over a standard box.
  const recordedOverhang = run?.counterOverhang;
  const counterFrontOverhang =
    standsAtMeasuredDepth(run) &&
    typeof recordedOverhang === 'number' &&
    recordedOverhang >= 0
      ? recordedOverhang
      : isVanityProfile
        ? vanityCounterFrontOverhang(runBodyDepth, profile.counterOverhang)
        : profile.counterOverhang;

  // Where the run dies into a wall and the fill left a strip too thin to be a
  // filler, the slab runs on to the wall (`scribeCounter.js`). After the end
  // pieces, which treat every counter piece as a body. Never on a VIRTUAL
  // wall: an island's or a peninsula's face is synthesized to the piece's own
  // length, so its ends read as `wall` to `fillZoneBounds` while there is
  // nothing there to scribe to — the counter would cover an open slot.
  for (let i = 0; i < run.segments.length; i += 1) {
    const bounds = state.fillBounds.get(i);
    const { wall } = run.segments[i];
    if (!bounds?.ends || wall.isVirtual) continue;
    state.counterHeightItems.push(
      ...scribeCounterItems(state.counterHeightItems, {
        wallId: wall.id,
        lo: bounds.lo,
        hi: bounds.hi,
        wallLen: getWallAxes(wall)?.wallLen ?? 0,
        ends: bounds.ends,
      })
    );
  }

  const builtCountertops = [
    ...buildCountertopRuns({
      walls,
      counterHeightItems: state.counterHeightItems,
      floorY,
      dims: {
        cabinetHeight: runBodyHeight(run),
        thickness: profile.counterThickness,
        // A VANITY counter may stand proud of a shallow carcass so its bowl
        // still has somewhere to hang (`counterOverhang.js`; user ruling
        // 2026-09-25 "the sink can extend beyond the cabinet"). The profile's
        // own overhang is the floor, so an ordinary vanity — and every
        // kitchen, which never takes this branch — is unchanged.
        overhang: counterFrontOverhang,
        label: profile.counterLabel,
      },
    }),
    // A kite carries its own slab — the wedge is not on either leg's line.
    ...kites.map((k) => k.countertop).filter(Boolean),
  ];
  // A seated peninsula arm is topped by ONE slab, knee overhang included, and
  // the run crossing its root stops at that slab's edge — settled by the
  // generator when it seated the arm, and by the same rule here, so a
  // re-solve rebuilds it rather than dropping the overhang
  // (`kitchen-assembly/seatedPeninsulaTop.js`).
  // The counter seam rule at every inside corner: each leg's slab ends where
  // the slabs' fronts cross at an angled corner (the kite's slab rebuilt to
  // meet them), and starts at the other's front at a square one, so the front
  // edge comes to a point and no two slabs stack (`cornerSlabSeams.js`).
  const kiteStops = oblique
    .filter((o) => o.kite)
    .map(({ cornerIndex, stopA, stopB }) => ({ cornerIndex, stopA, stopB }));
  const seamedCountertops = seamCornerSlabs({
    run,
    countertops: builtCountertops,
    oblique: kiteStops,
    squareCorners: [...lowerCorners]
      .filter(([, c]) => c.mode !== 'oblique' && c.mode !== 'outside')
      .map(([i]) => i),
    floorY,
    ctx,
    frontOverhang: counterFrontOverhang,
  });
  const countertops = seatRunArms(run, seamedCountertops, state.bays);

  // Procedural sink basin + faucet for each sink cabinet, parented to the run
  // countertop covering it (the countertop's `parents` list every cabinet it
  // spans, so the sink-cabinet → countertop match is exact). Same object shapes
  // the generator emits, so the existing renderers draw them.
  // Ids are keyed on the BAY (stable across re-solves), not on the cabinet
  // (content-keyed on its span start, so a 1 mm slide renamed the sink). An
  // existing basin / faucet with that id is REUSED — moved, its model and
  // customization kept — never rebuilt (layout-relationships §2.5).
  const existingById = new Map(
    (ctx.existingObjects || []).filter((o) => o?.id).map((o) => [o.id, o])
  );
  const reuse = (built) => {
    const prior = built && existingById.get(built.id);
    if (!prior) return built;
    return {
      ...prior,
      center: built.center,
      rotation: built.rotation,
      parents: built.parents,
      countertopId: built.countertopId,
      sinkCabinetId: built.sinkCabinetId ?? prior.sinkCabinetId,
      fixtureGroupId: built.fixtureGroupId ?? prior.fixtureGroupId,
      bayId: built.bayId,
    };
  };
  const sinkFixtures = [];
  // A vanity-profile run speaks the BATHROOM vocabulary: `sink` + `sinkType`
  // and a `bathroom-sink-faucet` via the vanity-assembly builders, so a
  // run-built vanity renders and restyles exactly like a build_vanity one.
  // Kitchen runs keep the kitchen builders unchanged. Same bay-keyed ids and
  // reuse() semantics either way.
  const isVanityRun = profile.id === VANITY_PROFILE_ID;
  for (const assoc of appl.sinkAssociations) {
    const ct = countertops.find((c) =>
      (c.parents || []).some((p) => p.id === assoc.cabinet.id)
    );
    if (!ct) continue;
    const sinkKey = assoc.bayId || `${assoc.applianceId}_${assoc.cabinet.id}`;
    const built = isVanityRun
      ? buildVanitySinkBasin({
          id: `sink_${sinkKey}`,
          wall: assoc.wall,
          range: assoc.range,
          floorY,
          countertopId: ct.id,
          sinkCabinetId: assoc.cabinet.id,
          fixtureGroupId: assoc.cabinet.fixtureGroupId,
          addedBy: 'manual-cabinet-run',
          // The base the basin sinks into, exactly as it was built.
          cabinetDepth: assoc.cabinet.dimensions?.z ?? profile.depth,
          // …and the counter it actually hangs from, which on a shallow
          // carcass stands proud of it.
          counterDepth:
            (assoc.cabinet.dimensions?.z ?? profile.depth) +
            counterFrontOverhang,
          cabinetHeight: runBodyHeight(run),
          counterThickness: profile.counterThickness,
        })
      : buildKitchenSinkBasin({
          id: `sink_${sinkKey}`,
          wall: assoc.wall,
          range: assoc.range,
          floorY,
          countertopId: ct.id,
          // The slab this basin drops into. The run HAS it — it was matched
          // one line up — so the basin is seated against its real front face
          // rather than against the standard depth, which is what lets it
          // follow a run built shallower or deeper.
          countertop: ct,
          sinkCabinetId: assoc.cabinet.id,
          fixtureGroupId: assoc.cabinet.fixtureGroupId,
          addedBy: 'manual-cabinet-run',
        });
    const basin = reuse(built && { ...built, bayId: assoc.bayId });
    if (!basin) continue;
    sinkFixtures.push(basin);
    const builtFaucet = isVanityRun
      ? buildVanityFaucet({
          id: `faucet_${sinkKey}`,
          basin,
          countertop: ct,
          floorY,
          cabinetHeight: runBodyHeight(run),
          counterThickness: profile.counterThickness,
          addedBy: 'manual-cabinet-run',
        })
      : buildKitchenFaucet({
          id: `faucet_${sinkKey}`,
          sink: basin,
          wall: assoc.wall,
          floorY,
          countertopId: ct.id,
          addedBy: 'manual-cabinet-run',
        });
    const faucet = reuse(builtFaucet && { ...builtFaucet, bayId: assoc.bayId });
    if (faucet) sinkFixtures.push(faucet);
  }
  // …and a microwave in each microwave cabinet (`microwaveCabinets.js`, the
  // user 2026-11-01: "put a microwave in"), keeping the model a user swapped
  // in
  const microwaves = [...state.cabinets, ...state.uppers]
    .map(microwaveFor)
    .filter(Boolean)
    .map((m) => {
      const prior = existingById.get(m.id);
      return prior
        ? {
            ...prior,
            center: m.center,
            rotation: m.rotation,
            parents: m.parents,
            microwaveCabinetId: m.microwaveCabinetId,
            ...(m.bayId ? { bayId: m.bayId } : {}),
            // a model the user picked keeps its own size; the placeholder
            // is sized to the opening it stands in
            ...(prior.modelId ? {} : { dimensions: m.dimensions }),
          }
        : m;
    });
  // A corner unit isn't an auto bay, so its Unit-scope appearance rides on a
  // per-corner override (cornerOverride) keyed by cornerIndex; absent one it
  // falls back to run ?? room like everything else. Tag the cabinet with the
  // resolved override id so the panel can re-find/update it on the next edit.
  for (const cu of cornerUnits) {
    const ov = cornerOverride(run, cu.cornerIndex);
    applyAppearanceBundle(cu, effectiveAppearance({ override: ov, run, ctx }));
    if (ov) cu.overrideId = ov.id;
  }
  // Sink bases have no per-bay override; they take the run/room appearance
  // (so a sink cabinet matches the rest of the run).
  const cornerAppearance = effectiveAppearance({ run, ctx });
  for (const sb of [...appl.sinkBases, ...appl.cooktopBases])
    applyAppearanceBundle(sb, cornerAppearance);
  // An upper resolves its OWN per-bay override, in the 'upper' tier — Unit scope
  // means THIS box, and an upper and the lower beneath it are two boxes. Before
  // tiers, uppers took the run/room appearance unconditionally while the panel
  // still offered them a Unit-scope edit: the override was anchored at the wall
  // param they share, so the edit landed on the LOWER and the upper never
  // changed. Corner uppers resolve through cornerOverride for the same reason
  // (they carry the corner lower's `cornerIndex`).
  const upperOverrideFor = new Map();
  // Records the override pass already seated on a box (`upperTier`): no
  // other box may wear them by their stored point.
  const seatedUpper = new Set(
    state.upperBays.map((ub) => ub.override?.id).filter(Boolean)
  );
  // the uppers the tier gave glass doors (`upperTier.glassFor`)
  const glassUppers = new Set(
    state.upperBays.filter((ub) => ub.glass).map((ub) => ub.cabinet)
  );
  for (const ub of state.upperBays) {
    const ov = governingOverride(
      run,
      ub.segIndex,
      { start: ub.start, end: ub.end },
      // The record the override pass seated on this box: a fixed-width
      // upper's own (it slid, so its anchor may stand in a neighbour by now)
      // or the width-less one that lives with it (`overrideHomes.js`).
      ub.override || null,
      'upper',
      seatedUpper
    );
    if (ov) upperOverrideFor.set(ub.cabinet, ov);
  }
  // An upper filler is a blank slab — re-force a flat front after the appearance
  // pass (a framed door style can't render in a thin strip; paint colour stays).
  const upperRecordOf = (u) =>
    u.cornerIndex != null
      ? cornerOverride(run, u.cornerIndex, 'upper')
      : upperOverrideFor.get(u) || null;
  // A stacked box wears the look of the upper it stands on and carries no
  // override of its own (`upperStack.js`, C21).
  const stackUnder = new Map(stackRow.map((s) => [s.cabinet, s.under]));
  for (const s of stackRow) if (s.glass) glassUppers.add(s.cabinet);
  for (const u of state.uppers) {
    const under = stackUnder.get(u);
    const ov = upperRecordOf(under || u);
    applyAppearanceBundle(u, effectiveAppearance({ override: ov, run, ctx }));
    // the short cabinet stacked with a rack wears its look but answers to no
    // record: the rack's says where it stands (`rackStacks.js`)
    if (ov && !under && !u.category?.rackCompanion) u.overrideId = ov.id;
    if (u.category && u.category.filler) u.doorStyle = 'flat';
    // Glass doors are the drawn STYLE, set after the bundle so the run's look
    // still decides the design and colour (`glassUppers`, plan C11–C16).
    if (glassUppers.has(u)) drawGlassDoors(u);
  }
  for (const u of state.unsatisfied) {
    if (u.kind === 'opening') continue;
    state.warnings.push({
      severity: 'warning',
      type: 'fit',
      bayId: u.bayId,
      message: unsatisfiedMessage(u),
    });
  }
  // A vendor line's drawer base is drawn as the part it sells (item #23),
  // and so is its sink cabinet (`sinkUnits.js`).
  vendorDrawerStacks(state.cabinets, run, profile.id);
  vendorSinkFronts(state.cabinets, run, profile.id);
  // …and each end shelf says which side stands open (`endShelves.js`)
  stampEndShelfSides([
    ...state.cabinets,
    ...state.fillers,
    ...state.uppers,
    ...appl.objects,
  ]);
  // …and each upper whose record asks for a glass holder carries one where it
  // fits (`glassHolders.js`)
  stampGlassHolders(run, state.uppers);
  // …and each angled end says which side stands open, and the counter over a
  // base one is cut at 45° to follow its face (`angledEnds.js`)
  stampAngledEndSides([
    ...state.cabinets,
    ...state.fillers,
    ...state.uppers,
    ...appl.objects,
  ]);
  const angledBases = state.cabinets.filter(
    (c) => isAngledEnd(c) && c.angledEndSide
  );
  const cutCountertops = angledBases.length
    ? countertops.map((ct) =>
        angledBases.reduce((acc, c) => cutCounterAtAngledEnd(acc, c), ct)
      )
    : countertops;
  const warnings = [...computeWarnings(run, ctx), ...state.warnings];

  return {
    cabinets: state.cabinets,
    fillers: state.fillers,
    uppers: state.uppers,
    cornerUnits,
    countertops: cutCountertops,
    appliancePlacements: [...appl.objects, ...sinkFixtures],
    microwaves,
    // The solved appliance-bay table: where each bay ENDED UP. A caller that
    // owns the appliance objects moves them here (layout-relationships §2.5).
    bays: state.bays,
    unsatisfied: state.unsatisfied,
    // Each segment's fill zone and what it packed around (`fillSegmentZone`):
    // the free stretch a bay may grow into, read by `bayReach.js`.
    zones: state.zones,
    linksApplied: state.linksApplied,
    // What the upper tier decided at each corner, what the user asked for
    // there and which answers fit (`upperCornerTreatment.js`) — the corner
    // control reads its options and their reasons from here, so the panel and
    // the fill cannot grade a corner differently.
    upperCorners: state.upperCorners || [],
    // …and its twin for the lower tier.
    lowerCorners: lowerCornerReport,
    // Each kite's cabinet stops, by corner — the bend step rebuilds a kite's
    // slab with them once a drawn island leg is finished
    // (`bentRunFill.bendCounterPatches`).
    kiteStops,
    warnings,
  };
}

module.exports = { fillRun, planSubSpanSequence, tallSpanSpec };
